/* ==========================================================================
   Create with AI — chat panel (sandbox-derived)

   Direct port of `.sandbox/create-with-ai/script.js` with two surgery points
   wired to the live theme:

     1. mockGenerate(prompt) → realGenerate(prompt). Drives the existing
        #generative-ai-dialog form (which generative-ai-image-modal.js owns).
        Sets #AISearchInput value, dispatches submit, then waits for figures
        to land in #generative-ai-images. Reads {url, fileName} off each
        figure's dataset and returns them in the {newImages: [...]} shape
        the original sandbox produced.

     2. insertToCanvas(tile, image) → realInsertToCanvas(tile, image, instanceKey).
        Routes by `data-ai-instance` value:
          - `dtfut` → cart/manage path (singleFileAddToCart + manageFiles/uploadFile,
            close dialog). Used by tab 3 "Create with AI".
          - `ds`    → fabric canvas path (presign upload + fabric.Image.fromURL +
            window.addObjectToCanvas). Used by Design Studio overlay AI panel.
        Default falls back to the cart path.

   Boots whichever instance(s) the panel renders, idempotent re-init for
   Shopify section/block reloads.
   ========================================================================== */

(function () {
  'use strict';

  if (window.__aiChatSandboxInit) return;
  window.__aiChatSandboxInit = true;

  // ---------- Config ----------
  // 3 outputs at a time — design constraint, locks the grid to a tidy 3-up
  // (or stacked 3 on narrow). Mirrors live's batchSize and the cap applied
  // by the dtfut fetch patch in dtf-upload-tabs.js.
  var SKELETON_COUNT  = 3;
  var AI_BATCH_SIZE   = 3;
  var AI_MAX_IMAGES   = 3;

  // Sticker-style products that only accept ONE image at a time. On these
  // products the recent-designs grid behaves like a radio set — picking a
  // second design auto-deselects the first instead of accumulating.
  var SINGLE_SELECT_PATHS = [
    '/products/vinyl-stickers',
    '/products/bumper-stickers',
    '/products/truck-stickers',
    '/products/indoor-floor-graphics',
    '/products/outdoor-floor-graphics',
    '/products/window-clings',
    '/products/low-tac-wall-graphics',
    '/products/custom-magnets',
    '/products/custom-soft-vinyl-keychains'
  ];
  function isSingleSelectMode() {
    var path = (window.location && window.location.pathname) || '';
    // Strip any trailing slash so `/products/foo/` matches `/products/foo`.
    if (path.length > 1 && path.charAt(path.length - 1) === '/') {
      path = path.slice(0, -1);
    }
    for (var i = 0; i < SINGLE_SELECT_PATHS.length; i++) {
      if (path === SINGLE_SELECT_PATHS[i]) return true;
    }
    return false;
  }

  // ---------- Result-tile lightbox (magnifier zoom) ----------
  // One <body>-level modal, reused by every tile and every panel instance, that
  // shows the full-resolution generated image. Appended to <body> (never inside
  // the panel) so an ancestor transform/overflow can't trap or clip it — same
  // fix as the iOS fixed-position popovers elsewhere. Built lazily on first open;
  // CSS gives it the panel's max z-index so it sits above the AI modal.
  // onUse (Justin 2026-09-08): the zoom carries a "Use This Design" CTA at the bottom of the
  // image — the caller passes what "use" means for that tile (its own click).
  function openAiZoom(src, onUse) {
    if (!src) return;
    var modal = document.getElementById('ai-pop-zoom-modal');
    if (!modal) {
      modal = document.createElement('div');
      modal.id = 'ai-pop-zoom-modal';
      modal.className = 'ai-pop-zoom';
      modal.innerHTML =
        '<div class="ai-pop-zoom__backdrop" data-ai-zoom-close></div>'
        + '<div class="ai-pop-zoom__dialog" role="dialog" aria-modal="true" aria-label="Design preview">'
        +   '<button type="button" class="ai-pop-zoom__close" data-ai-zoom-close aria-label="Close preview">'
        +     '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>'
        +   '</button>'
        +   '<img class="ai-pop-zoom__img" alt="Design preview">'
        +   '<button type="button" class="ai-pop-zoom__use" data-ai-zoom-use>Use This Design &rarr;</button>'
        + '</div>';
      document.body.appendChild(modal);
      modal.querySelector('[data-ai-zoom-use]').addEventListener('click', function (e) {
        e.preventDefault();
        var fn = modal.__onUse;
        closeAiZoom();
        if (typeof fn === 'function') fn();
      });
      modal.addEventListener('click', function (e) {
        // Close on everything EXCEPT a click on the preview image itself — this
        // covers the X, the backdrop, and the empty area around the image. On
        // mobile the dialog fills the screen (so there's no backdrop left to
        // hit); keying off "not the image" is what makes tap-outside-to-close
        // work there too, not just on desktop.
        if (e.target && e.target.closest && e.target.closest('.ai-pop-zoom__img')) return;
        closeAiZoom();
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') closeAiZoom();
      });
    }
    // Loading state until the full-size image has decoded (Justin 2026-09-08) —
    // the v2 sheet paints a spinner/scan card on .is-loading; elsewhere it is inert.
    var zimg = modal.querySelector('.ai-pop-zoom__img');
    modal.__onUse = typeof onUse === 'function' ? onUse : null;
    modal.classList.toggle('has-use', !!modal.__onUse);
    modal.classList.add('is-loading');
    zimg.onload = zimg.onerror = function () { modal.classList.remove('is-loading'); };
    zimg.src = src;
    if (zimg.complete && zimg.naturalWidth) modal.classList.remove('is-loading');
    modal.classList.add('is-open');
    document.body.classList.add('ai-pop-zoom-open');
  }
  function closeAiZoom() {
    var modal = document.getElementById('ai-pop-zoom-modal');
    if (modal) modal.classList.remove('is-open');
    document.body.classList.remove('ai-pop-zoom-open');
  }

  // ---------- Predictable pill outputs ----------
  // The three example pills ("Colorful gym logo", "Floral mama design",
  // "Groovy party design") must always return the SAME curated designs in
  // every environment. Previously fakeGenerate() fetched live from a
  // third-party cache (rushordertees.com) and, on any miss/CORS/offline,
  // fell back to RANDOM picsum tiles — so the same button produced
  // different (and often broken) images. This map pins each pill's prompt
  // to a fixed set of Ninja-hosted image URLs. It's keyed by the pill's
  // prompt text (whitespace-normalized + lowercased), so this ONE map
  // serves every template that reuses the identical pill markup
  // (dtf-ai-modal, ai-image-creator, dtf-upload-tabs, v0-editor).
  var PILL_PROMPTS = {
    gym:   'A modern logo of a bold dumbbell icon in bright electric blue, hot pink, and neon yellow with text that says "Common Ground Athletics" on a pure white background',
    mama:  'A bold modern design of a large floral bouquet in vivid pink, coral, and sage with text that says "Mama" in chunky serif on a white background',
    party: 'A vibrant party design of streamers and stars in hot pink, electric orange, neon green, and bright blue with text that says "Let\'s Party" in wavy groovy letters on a pure white background'
  };

  var PILL_IMAGE_SETS = {
    gym: [
      'https://ninjauploads-production.imgix.net/a3a8811c0707512c500b4337b1cbd5801eb8f3a721ad2544fb7cce8f675d758e____image_1780430781887_545.png?trim=colorUnlessAlpha&w=2000',
      'https://ninjauploads-production.imgix.net/3e67ee6a61a6891639bf6c2ecbb8c64ad6d1b5c37645f69fc80587c757ef1d15____image_1780430820896_525.png?trim=colorUnlessAlpha&w=2000',
      'https://ninjauploads-production.imgix.net/f77a8eac814f6478a1203f7b363f5db5ce10fccdce03371111d3610ee947ac78____image_1780430840270_190.png?trim=colorUnlessAlpha&w=2000'
    ],
    mama: [
      'https://ninjauploads-production.imgix.net/732ae824daa1c0aa6e7da4093aab334c5517d94b9a48ac4bd0331e92a318ee2f____image_1780430667741_923.png?trim=colorUnlessAlpha&w=2000',
      'https://ninjauploads-production.imgix.net/4fb5bc31443ede2ce1c725785b5202617b3a22e7e6f4171b5ab368e166c8b537____image_1780430685691_187.png?trim=colorUnlessAlpha&w=2000',
      'https://ninjauploads-production.imgix.net/30cd361986746ac1387fe6245f3c1482b987da91f689cf85d29a4304ac4b98f5____image_1780430702864_542.png?trim=colorUnlessAlpha&w=2000'
    ],
    party: [
      'https://ninjauploads-production.imgix.net/dcdf2c88bb7486e53d618c3560f81c098f317d8d3d97be2747ebacc372c7ebfc____image_1780428185450_973.png?trim=colorUnlessAlpha&w=2000',
      'https://ninjauploads-production.imgix.net/cd13ffc36cfb8a587eb55cd9d886e1e364ba85477f6e08f48319285e8342511c____image_1780428588278_435.png?trim=colorUnlessAlpha&w=2000',
      'https://ninjauploads-production.imgix.net/5eb7b78bc6677a05f994e1612d3fb12ff188e440f0724484a3d5693fa84df245____image_1780428622803_1.png?trim=colorUnlessAlpha&w=2000'
    ]
  };

  function normalizePillKey(s) {
    return (s || '').replace(/\s+/g, ' ').trim().toLowerCase();
  }

  var PILL_IMAGE_MAP = {};
  Object.keys(PILL_PROMPTS).forEach(function (slug) {
    PILL_IMAGE_MAP[normalizePillKey(PILL_PROMPTS[slug])] =
      PILL_IMAGE_SETS[slug].map(function (url, i) {
        return { url: url, thumbnailUrl: url, fileName: slug + '-design-' + (i + 1) + '.png' };
      });
  });

  // Returns the curated 3-image set for a known pill prompt, or null.
  function getPillImages(prompt) {
    return PILL_IMAGE_MAP[normalizePillKey(prompt)] || null;
  }

  // ---------- Surgery point #1: real generate ----------
  // Drives #generative-ai-dialog form (lives in DOM via `aiImageGenerator`
  // content-for in dtf-upload-tabs.liquid). Returns the same shape as the
  // sandbox's mockGenerate: { stats, existing, newImages }. Resolves once
  // figures land in #generative-ai-images, or rejects on a stall.
  function realGenerate(prompt, batchSize) {
    var maxImages = batchSize || AI_MAX_IMAGES;
    return new Promise(function (resolve, reject) {
      var nativeInput = document.getElementById('AISearchInput');
      var nativeForm  = document.querySelector('#generative-ai-dialog > form');
      var imagesEl    = document.getElementById('generative-ai-images');
      if (!nativeInput || !nativeForm || !imagesEl) {
        reject(new Error('AI dialog not in DOM'));
        return;
      }

      // Empty the figures up front — the native submit handler does this too,
      // but doing it here means our MutationObserver will fire when the next
      // batch lands instead of seeing pre-existing children and resolving
      // immediately.
      imagesEl.innerHTML = '';

      // Snapshot which URLs were already in history before this submit so we
      // can skip them when reading the figures (the native handler appends
      // both "existing" canned + "new" generated figures into the same node).
      // In practice imagesEl is empty after the native handler clears it,
      // but leave this in for defense.
      var preExisting = new Set();

      // Set up the observer FIRST so we don't miss the append.
      var resolved = false;
      var stallTimer = null;
      var settleTimer = null;

      function readFigures() {
        var figs = imagesEl.querySelectorAll('figure');
        var newImages = [];
        for (var i = 0; i < figs.length; i++) {
          var f = figs[i];
          var url = f.dataset.url;
          if (!url || preExisting.has(url)) continue;
          newImages.push({
            url: url,
            thumbnailUrl: (f.querySelector('img') && f.querySelector('img').src) || url,
            fileName: f.dataset.fileName || ('ai-image-' + Date.now() + '-' + i + '.png'),
            // Keep a reference to the original figure so the click flow can
            // dispatch through the native delegated handler if it wants — we
            // don't currently use it but it's cheap.
            __figure: f
          });
        }
        return newImages.slice(0, maxImages);
      }

      function finish() {
        if (resolved) return;
        resolved = true;
        clearTimeout(stallTimer);
        clearTimeout(settleTimer);
        try { mo.disconnect(); } catch (_) {}
        var newImages = readFigures();
        resolve({
          stats: { remainingRequest: Number.MAX_SAFE_INTEGER },
          existing: [],
          newImages: newImages
        });
      }

      var mo = new MutationObserver(function () {
        // Wait for the figures to settle — the native handler appends
        // existing canned figures, then later appends new generated ones.
        // Debounce briefly so we resolve once both batches are in.
        clearTimeout(settleTimer);
        if (imagesEl.querySelectorAll('figure').length === 0) return;
        // Also wait for the native loader to clear, indicating both stages
        // (existing + generate-new) are done.
        var loader = document.getElementById('generative-ai-dialog-loader');
        var loaderDone = !loader || !loader.classList.contains('show');
        var delay = loaderDone ? 50 : 350;
        settleTimer = setTimeout(finish, delay);
      });
      mo.observe(imagesEl, { childList: true });

      // Hard timeout (60s) so we don't leak the observer on a network failure.
      stallTimer = setTimeout(function () {
        if (resolved) return;
        resolved = true;
        try { mo.disconnect(); } catch (_) {}
        reject(new Error('AI generate timed out'));
      }, 60000);

      // Also resolve when the loader finishes — covers the case where existing
      // figures already filled the grid and no further mutation will happen.
      var loaderEl = document.getElementById('generative-ai-dialog-loader');
      if (loaderEl) {
        var loaderObs = new MutationObserver(function () {
          if (resolved) { loaderObs.disconnect(); return; }
          if (!loaderEl.classList.contains('show') &&
              imagesEl.querySelectorAll('figure').length > 0) {
            loaderObs.disconnect();
            finish();
          }
        });
        loaderObs.observe(loaderEl, { attributes: true, attributeFilter: ['class'] });
      }

      // Drive the form. The native handler's submit listener picks this up
      // and reads the batch size we want from the form (defaults to 3).
      if (batchSize) nativeForm.setAttribute('data-batch-size', String(batchSize));
      nativeInput.value = prompt;
      nativeInput.dispatchEvent(new Event('input', { bubbles: true }));
      nativeInput.dispatchEvent(new Event('change', { bubbles: true }));
      var jq = window.jQuery || window.$;
      try {
        if (jq && typeof jq(nativeForm).trigger === 'function') {
          jq(nativeForm).trigger('submit');
        } else if (typeof nativeForm.requestSubmit === 'function') {
          nativeForm.requestSubmit();
        } else {
          nativeForm.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
        }
      } catch (err) {
        if (resolved) return;
        resolved = true;
        clearTimeout(stallTimer);
        try { mo.disconnect(); } catch (_) {}
        reject(err);
      }
    });
  }

  // ---------- Surgery point #2: real insert action ----------
  // Branches on the instance's `data-ai-instance` value:
  //   * `dtfut`  → cart/manage path (singleFileAddToCart + manageFiles/uploadFile,
  //                close dialog). This is the tab-3 "Create with AI" panel — the
  //                generated image flows into the upload pipeline so the regular
  //                order-selection UI takes over.
  //   * `ds`     → canvas path (fabric.Image.fromURL + window.addObjectToCanvas).
  //                This is the Design Studio overlay AI panel — the generated
  //                image lands on the live fabric canvas, not the cart.
  // Default falls back to the cart path so older instances keep working.
  function realInsertToCanvas(tile, image, instanceKey, cascadeIndex) {
    if (tile.classList && tile.classList.contains('is-loading')) {
      return Promise.resolve();
    }
    if (tile.classList) tile.classList.add('is-loading');

    if (instanceKey === 'ds') {
      return insertOntoFabricCanvas(tile, image, cascadeIndex);
    }
    if (instanceKey === 'aipage') {
      return stashAndHandoff(image);
    }
    return insertViaCartPipeline(tile, image);
  }

  // Standalone AI page (/pages/ai-image-creator) has no upload pipeline —
  // stash the chosen image in sessionStorage and navigate to /products/dtf-transfers,
  // where dtf-upload-tabs.js consumes the stash and routes it into the real
  // upload pipeline. Mirrors the multi-select handler in dtf-ai-page.js but
  // for a single tile click.
  function stashAndHandoff(image) {
    try {
      sessionStorage.setItem('dtf:pending-ai-uploads:v1', JSON.stringify({
        items: [{
          url: image.url,
          thumbnailUrl: image.thumbnailUrl || image.url,
          fileName: image.fileName || ''
        }],
        ts: Date.now()
      }));
    } catch (_) {}
    var t = document.createElement('div');
    t.style.cssText = 'position:fixed;left:50%;bottom:32px;transform:translateX(-50%);background:#0f172a;color:#fff;font:600 14px/1.2 "DM Sans",system-ui,sans-serif;padding:14px 22px;border-radius:999px;box-shadow:0 12px 32px rgba(15,23,42,0.25);z-index:99999;pointer-events:none;';
    t.textContent = 'Sending design to your DTF order…';
    document.body.appendChild(t);
    setTimeout(function () { window.location.assign('/products/dtf-transfers'); }, 250);
    return Promise.resolve();
  }

  // Cart/manage path (MULTI) — when the user multi-selects from Recently
  // Generated and hits "Use Designs", route each URL through the same picker
  // pipeline live uses for "Previous Designs" multi-select (see
  // snippets/product-page-popups-multiupload.liquid `md-picker:use-designs`):
  //   createFileObjectFromUrl(url) → stub File + .fileURL → multiFilesManageByURL(files)
  // multiFilesManageByURL auto-activates the multi-upload popup when files.length > 1
  // and works on BOTH desktop and mobile (the manageFiles path was desktop-only).
  function insertMultipleViaCartPipeline(entries) {
    var loader = document.getElementById('generative-ai-dialog-loader');
    var form = document.querySelector('#generative-ai-dialog > form');
    if (loader) loader.classList.add('show');
    if (form) form.setAttribute('disabled', 'disabled');

    return Promise.resolve().then(function () {
      var urls = entries.map(function (e) { return e.url; }).filter(Boolean);
      if (!urls.length) return;

      // Path A: live's URL-based multi pipeline (bySize_popular.js).
      if (typeof window.createFileObjectFromUrl === 'function' &&
          (typeof window.multiFilesManageByURL === 'function' ||
           typeof window.singleFileManageByURL === 'function')) {
        var files = urls.map(function (u) {
          var detail = window.createFileObjectFromUrl(u);
          detail.fileURL = u;
          detail.isAIImage = true;
          return detail;
        });
        var _uploadType = (typeof window.uploadType !== 'undefined') ? window.uploadType : '';
        var addToCart = function () {
          return (typeof window.singleFileAddToCart === 'function')
            ? Promise.resolve(window.singleFileAddToCart())
            : Promise.resolve();
        };
        if (files.length === 1) {
          if (_uploadType === 'multi' && typeof window.multiFilesManageByURL === 'function') {
            window.multiFilesManageByURL(files);
          } else if (typeof window.singleFileManageByURL === 'function') {
            return addToCart().then(function () { window.singleFileManageByURL(files[0]); });
          } else if (typeof window.multiFilesManageByURL === 'function') {
            window.multiFilesManageByURL(files);
          }
        } else if (typeof window.multiFilesManageByURL === 'function') {
          if (_uploadType === 'single') {
            return addToCart().then(function () { window.multiFilesManageByURL(files); });
          }
          window.multiFilesManageByURL(files);
        }
        return;
      }

      // Path B: fallback for PDPs without the URL-based helpers — fetch each
      // URL into a File blob and hand the array to manageFiles. Note: the
      // multi-upload popup may not engage on mobile via this path; only used
      // when Path A's helpers are absent (rare).
      return Promise.all(urls.map(function (u, i) {
        var entry = entries[i];
        var fileName = (entry && entry.fileName) ||
          ('ai-image-' + Date.now() + '-' + Math.floor(Math.random() * 10000) + '.png');
        return fetch(u).then(function (r) {
          var type = r.headers.get('Content-Type') || 'image/png';
          return r.arrayBuffer().then(function (ab) {
            var blob = new Blob([ab], { type: type });
            var file = new File([blob], fileName, { type: type });
            file.isAIImage = true;
            return file;
          });
        });
      })).then(function (files) {
        if (typeof window.manageFiles === 'function') {
          window.manageFiles(files);
        }
      });
    }).then(function () {
      window.isAIImage = true;
      var dialog = document.getElementById('generative-ai-dialog');
      if (dialog && typeof dialog.close === 'function') {
        try { dialog.close(); } catch (_) {}
      }
    }).catch(function (err) {
      console.error('ai-chat-sandbox multi-cart insert failed', err);
    }).then(function () {
      if (loader) loader.classList.remove('show');
      if (form) form.removeAttribute('disabled');
    });
  }

  // Cart/manage path — verbatim copy of the original tab-3 behavior.
  function insertViaCartPipeline(tile, image) {
    var loader = document.getElementById('generative-ai-dialog-loader');
    var form = document.querySelector('#generative-ai-dialog > form');
    if (loader) loader.classList.add('show');
    if (form) form.setAttribute('disabled', 'disabled');

    var jq = window.jQuery || window.$;
    var url = image.url;
    var fileName = image.fileName ||
      ('ai-image-' + Date.now() + '-' + Math.floor(Math.random() * 10000) + '.png');

    /* Multi-upload mode: just merge the chosen image into the existing
       multi-upload-wrapper. No addToCart (round-1 isn't a "single active
       design" — it's already represented in the wrapper as a block) and no
       singleFileManage replace. Skip the fetch+blob path entirely; route
       through the URL-based pipeline like the previous-uploads picker does
       in `.use_designes_bulk` for uploadType === 'multi'. After files land,
       auto-scroll the multi-upload-wrapper to the first new block, mirroring
       what `.use_designes_bulk` does at bySize.js:587–595. */
    if (document.querySelector('multi-upload-wrapper.active') &&
        typeof window.createFileObjectFromUrl === 'function' &&
        typeof window.multiFilesManageByURL === 'function') {
      try {
        var beforeBlocks = jq ? jq('multi-upload uploaded-files-block').length : 0;
        var detail = window.createFileObjectFromUrl(url);
        detail.fileURL = url;
        detail.isAIImage = true;
        window.multiFilesManageByURL([detail]);
        setTimeout(function () {
          if (typeof window.setFocusOfMultiUpload === 'function') {
            window.setFocusOfMultiUpload(beforeBlocks);
          }
        }, 600);
      } catch (e) {
        console.error('ai-chat-sandbox multi-mode insert failed', e);
      }
      if (loader) loader.classList.remove('show');
      if (form) form.removeAttribute('disabled');
      if (tile.classList) tile.classList.remove('is-loading');
      var dialog = document.getElementById('generative-ai-dialog');
      if (dialog && typeof dialog.close === 'function') {
        try { dialog.close(); } catch (_) {}
      }
      return Promise.resolve();
    }

    return fetch(url)
      .then(function (response) {
        var type = response.headers.get('Content-Type') || 'image/png';
        return response.arrayBuffer().then(function (ab) {
          return { type: type, buffer: ab };
        });
      })
      .then(function (data) {
        var blob = new Blob([data.buffer], { type: data.type });
        var file = new File([blob], fileName, { type: data.type });
        var addToCartPromise = Promise.resolve();
        if (jq && jq('upload-controls master-upload').hasClass('hidden') &&
            typeof window.singleFileAddToCart === 'function') {
          addToCartPromise = window.singleFileAddToCart();
        }
        return addToCartPromise.then(function () { return file; });
      })
      .then(function (file) {
        var isNewBySize = jq ? jq('upload-controls').length : 0;
        if (isNewBySize > 0 && typeof window.manageFiles === 'function') {
          file.isAIImage = true;
          window.manageFiles([file]);
        } else if (typeof window.uploadFile === 'function') {
          if (jq && jq('.step__2').hasClass('hidden')) {
            window.uploadFile(file);
          } else {
            window.uploadFile(file, 'second_image');
          }
        }
        window.isAIImage = true;
        var dialog = document.getElementById('generative-ai-dialog');
        if (dialog && typeof dialog.close === 'function') {
          try { dialog.close(); } catch (_) {}
        }
      })
      .catch(function (err) {
        console.error('ai-chat-sandbox cart insert failed', err);
      })
      .then(function () {
        if (loader) loader.classList.remove('show');
        if (form) form.removeAttribute('disabled');
        if (tile.classList) tile.classList.remove('is-loading');
      });
  }

  // Canvas path — fetches the generated image, presigns + uploads (so the URL
  // is on the studio's CDN), then drops a fabric.Image onto window.fabricCanvas
  // via window.addObjectToCanvas. Mirrors the legacy DS-panel `insertToCanvas`
  // that previously lived in v0-script.js.
  function insertOntoFabricCanvas(tile, image, cascadeIndex) {
    var url = image.url;
    var fileName = image.fileName ||
      ('ai-image-' + Date.now() + '-' + Math.floor(Math.random() * 10000) + '.png');
    /* When the user multi-selects from Recently Generated and hits "Use
       Designs", each tile lands at the same default canvas position which
       makes them perfectly overlap. Offset each subsequent image by 30px
       diagonally so all selections are visible after insertion. The first
       tile (index 0) lands at the default position. */
    var cascadeOffset = (cascadeIndex && cascadeIndex > 0) ? cascadeIndex * 30 : 0;

    return fetch(url)
      .then(function (r) { return r.blob(); })
      .then(function (blob) {
        var fileType = blob.type || 'image/png';
        if (typeof window.getPresignedUploadUrl !== 'function') {
          throw new Error('getPresignedUploadUrl unavailable');
        }
        return window.getPresignedUploadUrl(fileName, fileType).then(function (presign) {
          if (!presign || !presign.url || !presign.sourceUrl) {
            throw new Error('Invalid presigned URL');
          }
          return fetch(presign.url, {
            method: 'PUT',
            headers: { 'Content-Type': fileType },
            body: blob
          }).then(function (uploadRes) {
            if (!uploadRes.ok) throw new Error('Upload failed');
            return presign.sourceUrl;
          });
        });
      })
      .then(function (uploadedUrl) {
        var canvas = window.fabricCanvas;
        if (!canvas) throw new Error('Canvas not ready');
        if (typeof window.fabric === 'undefined' || !window.fabric.Image) {
          throw new Error('fabric.Image unavailable');
        }
        return new Promise(function (resolve, reject) {
          window.fabric.Image.fromURL(uploadedUrl, function (fabricImg) {
            if (!fabricImg) { reject(new Error('fabric.Image.fromURL returned null')); return; }
            var maxSize = 300;
            var scale = Math.min(maxSize / fabricImg.width, maxSize / fabricImg.height, 1);
            fabricImg.set({ scaleX: scale, scaleY: scale, selectable: true, hasControls: true });
            fabricImg._originalUrl = uploadedUrl;
            fabricImg._bgRemoved = false;
            fabricImg._aiGenerated = true;
            if (typeof window.addObjectToCanvas === 'function') {
              window.addObjectToCanvas(fabricImg, canvas);
            } else {
              canvas.add(fabricImg);
              canvas.setActiveObject(fabricImg);
              canvas.requestRenderAll();
            }
            /* Apply cascade offset AFTER addObjectToCanvas/canvas.add so we
               override the default centering. Each subsequent multi-select
               tile shifts 30px right + 30px down. */
            if (cascadeOffset > 0) {
              fabricImg.set({
                left: (fabricImg.left || 0) + cascadeOffset,
                top:  (fabricImg.top  || 0) + cascadeOffset
              });
              fabricImg.setCoords();
              canvas.requestRenderAll();
            }
            resolve();
          }, { crossOrigin: 'anonymous' });
        });
      })
      .catch(function (err) {
        console.error('ai-chat-sandbox canvas insert failed', err);
      })
      .then(function () {
        if (tile.classList) tile.classList.remove('is-loading');
      });
  }

  // ---------- localStorage history (shared across all AI instances) ----------
  // Both the DS overlay (instanceKey="ds") and the dtfut Tab-3 (instanceKey="dtfut")
  // read/write the same key so Recent Generated stays in sync between them.
  var HISTORY_KEY_BASE = 'ninjaAiHistory';
  var HISTORY_TTL_MS   = 30 * 24 * 60 * 60 * 1000;
  var HISTORY_MAX      = 30;

  // One-time migration: merge any old per-instance histories into the shared key
  // on first load, so users don't lose their existing Recent Generated entries.
  (function migrateLegacyHistory() {
    try {
      if (localStorage.getItem(HISTORY_KEY_BASE)) return;
      var merged = [];
      ['ds', 'dtfut', 'default'].forEach(function (k) {
        var raw = localStorage.getItem(HISTORY_KEY_BASE + ':' + k);
        if (!raw) return;
        try { merged = merged.concat(JSON.parse(raw) || []); } catch (_) {}
      });
      if (!merged.length) return;
      var seen = {};
      merged = merged.filter(function (e) {
        if (!e || !e.url || seen[e.url]) return false;
        seen[e.url] = 1;
        return true;
      });
      merged.sort(function (a, b) { return (b.savedAt || 0) - (a.savedAt || 0); });
      localStorage.setItem(HISTORY_KEY_BASE, JSON.stringify(merged.slice(0, HISTORY_MAX)));
    } catch (_) {}
  })();

  function loadHistory(_instanceKey) {
    try {
      var raw = localStorage.getItem(HISTORY_KEY_BASE);
      var list = raw ? JSON.parse(raw) : [];
      var cutoff = Date.now() - HISTORY_TTL_MS;
      return list.filter(function (e) { return e && e.savedAt && e.savedAt > cutoff; });
    } catch (_) { return []; }
  }
  function saveHistory(_instanceKey, list) {
    try {
      localStorage.setItem(HISTORY_KEY_BASE, JSON.stringify(list.slice(0, HISTORY_MAX)));
    } catch (_) {}
    // Notify any other AI instances on the same page so they re-render their
    // Recently Generated grid. localStorage writes don't fire `storage` events
    // in the originating page, so we use a custom event instead.
    try { document.dispatchEvent(new CustomEvent('ninjaAiHistory:changed')); } catch (_) {}
  }

  // ---------- AI Edit: re-host helper ----------
  // Edited images come back on the AI-edit provider's ephemeral CDN. The
  // single-design insert path re-hosts via manageFiles, but the multi-design
  // path (addFilesASMultiBlock in bySize_popular.js) references the raw fileURL
  // with no re-host — so that ephemeral provider URL would expire out of a multi-design
  // print file. Re-host the edited image onto Ninja's store HERE, before it
  // enters the grid, so every downstream insert path carries a durable asset.
  // Reuses the same window.getPresignedUploadUrl + PUT the Design-Studio path
  // uses (insertOntoFabricCanvas above). Returns the imgix display URL
  // (ninjauploads-production.imgix.net/<key>), matching what manageFiles
  // produces for created images.
  function rehostEditedImage(srcUrl) {
    var NINJA_IMGIX_HOST = 'ninjauploads-production.imgix.net'; // == bySize_popular.js ninjaImgixHost
    if (typeof window.getPresignedUploadUrl !== 'function') {
      return Promise.reject(new Error('getPresignedUploadUrl unavailable'));
    }
    return fetch(srcUrl)
      .then(function (r) {
        if (!r.ok) throw new Error('Fetch edited image failed (' + r.status + ')');
        return r.blob();
      })
      .then(function (blob) {
        var fileType = blob.type || 'image/png';
        var key = 'ai-edit____image_' + Date.now() + '_' + Math.floor(Math.random() * 1000) + '.png';
        return window.getPresignedUploadUrl(key, fileType).then(function (presign) {
          if (!presign || !presign.url || !presign.sourceUrl) {
            throw new Error('Invalid presigned URL');
          }
          return fetch(presign.url, {
            method: 'PUT',
            headers: { 'Content-Type': fileType },
            body: blob
          }).then(function (uploadRes) {
            if (!uploadRes.ok) throw new Error('Upload failed (' + uploadRes.status + ')');
            return { url: 'https://' + NINJA_IMGIX_HOST + '/' + key, fileName: key };
          });
        });
      });
  }

  // ---------- Per-instance wiring ----------
  function attach(container) {
    if (container.__aiChatBound) return;
    container.__aiChatBound = true;

    var panel       = container.querySelector('[data-ai-panel]') || container;
    // v2 panels ask for 4 concepts (`data-ai-batch="4"`); everything else keeps 3.
    var batchSize   = parseInt(panel.getAttribute('data-ai-batch'), 10) || AI_BATCH_SIZE;
    // v2 look (Justin 2026-09-08): no chat-conversation UI — the prompt becomes a
    // plain caption over its result grid and the assistant text bubbles are dropped.
    var isV2        = panel.classList.contains('ai-chat--v2');
    var textarea    = container.querySelector('[data-ai-prompt]');
    var errorBox    = container.querySelector('[data-ai-error]');
    var generateBtn = container.querySelector('[data-ai-generate]');
    var thread      = container.querySelector('[data-ai-thread]');
    var pillsRow    = container.querySelector('[data-ai-pills]');
    var history     = container.querySelector('[data-ai-history]');
    var historyGrid = container.querySelector('[data-ai-history-grid]');
    var historyClear= container.querySelector('[data-ai-history-clear]');
    var historyToggle = container.querySelector('[data-ai-history-toggle]');
    var historyToggleLabel = container.querySelector('[data-ai-history-toggle-label]');
    var clearBtn    = container.querySelector('[data-ai-clear]');
    if (!panel || !textarea || !generateBtn || !thread) return;
    var wrap = textarea.closest('.ai-chat-input-wrap');
    // v2 panels: live "n / max" counter under the prompt (element + maxlength opt-in).
    var countEl = container.querySelector('[data-ai-count]');
    var maxLen = parseInt(textarea.getAttribute('maxlength'), 10) || 0;
    function syncCount() { if (countEl) countEl.textContent = textarea.value.length + (maxLen ? ' / ' + maxLen : ''); }
    textarea.addEventListener('input', syncCount);
    syncCount();
    var instanceKey = container.getAttribute('data-ai-instance') || 'default';

    // AI Edit mode: when set, the composer refines THIS source image via
    // NB instead of creating a new design. Cleared after submit/cancel.
    var editSourceUrl = null;
    var sendLabelEl = generateBtn.querySelector('.ai-chat-send-label');
    var defaultPromptPlaceholder = textarea.getAttribute('placeholder') || '';

    // Edit shows wherever it can ACTUALLY run — gated PURELY on the editor global
    // being present, with NO instanceKey allowlist. The global's presence is the
    // single control: window.nbEditRequest is exposed only by
    // bySize_popular.js (the by-size PDP) and the shared NB-edit.js (loaded
    // on the Design Studio + AI Image Generator sections), so the button lights up
    // on exactly those AI surfaces — PDP (dtfut), PDP-overlay DS (ds), portal Design
    // Studio (ds), and the AI Image Generator (aipage) — and any future surface that
    // loads the global self-corrects, with no allowlist to maintain. A page that has
    // the global but no .ai-chat panel (e.g. the members portal, which loads
    // bySize_popular.js) shows nothing — Edit only renders inside an AI panel.
    //
    // ⚠ Evaluated FRESH at render/completion time (createTile / renderHistory /
    // completion copy) — NOT cached here — because attach() runs at DOMContentLoaded
    // and window.nbEditRequest may be defined slightly later (or never).
    // Button + copy both call hasEditAffordance() so they always agree on a surface.
    function hasEditAffordance() {
      return typeof window.nbEditRequest === 'function';
    }
    var EDIT_DONE_MSG = 'Each prompt makes a fresh set of designs. To refine one, hit Edit and describe your change.';
    var NOEDIT_DONE_MSG = 'Each prompt generates a completely new image. To make changes, describe the full image again with your updates.';
    function generationDoneMsg() { return hasEditAffordance() ? EDIT_DONE_MSG : NOEDIT_DONE_MSG; }

    // ---------- Narrow-mode driver ----------
    // Single source of truth for narrow styling: the `.ai-chat--narrow` class
    // on the panel. JS toggles it based on the panel's own measured width.
    // Honors `data-ai-narrow-mode="force"` for panels that should always be
    // narrow regardless of width (the DS overlay panel is wide but UX-cramped
    // because of the canvas next to it, so it forces narrow). Mirrors
    // `.sandbox/create-with-ai/script.js` so sandbox + studio drive narrow
    // identically.
    var NARROW_BREAKPOINT = 500;
    var forceNarrow = panel.getAttribute('data-ai-narrow-mode') === 'force';
    function syncNarrow() {
      if (forceNarrow) {
        panel.classList.add('ai-chat--narrow');
        return;
      }
      var w = panel.getBoundingClientRect().width;
      // v2's two columns (prompt box | concepts) hold down to ~600px (the PDP
      // upload card's panel is ~650); below that it stacks through the same narrow
      // class (a @container rule can't target the container element itself, and
      // .ai-chat IS the container).
      var bp = isV2 ? 600 : NARROW_BREAKPOINT;
      panel.classList.toggle('ai-chat--narrow', w > 0 && w < bp - 1);
    }
    syncNarrow();
    try { new ResizeObserver(syncNarrow).observe(panel); } catch (_) {}
    // v2 (Justin 2026-09-08 "same height as the images next to it"): while a run is in
    // flight the prompt box is sized to the FIRST tile of the current row (the square
    // skeleton), not to the row — the row also carries the grid's scroll padding and,
    // for edits, the finished tiles' CTA rows. Published as a CSS var the sheet reads.
    // (the in-flight box is hidden now — the skeleton tiles carry the loading — so no
    // tile-height sync is needed; Justin 2026-09-08)

    // v2 (Justin 2026-09-08): once a generation starts, the prompt box gives way to a
    // dashed "Not quite right? Try a different description." card with "✦ Generate new";
    // that button brings the empty, focused box back. Edit mode keeps working: the
    // in-tile box is exempt from the hide and the card steps aside while editing.
    if (isV2) {
      var composerEl0 = container.querySelector('.ai-chat-composer');
      if (composerEl0) {
        var regenEl = document.createElement('div');
        regenEl.className = 'ai-chat-v2__regen';
        // a small button on the caption row's right (Justin 2026-09-08 — the box's
        // column is gone: one full-width row of 4 concepts)
        // refresh glyph + text, no outline — it rides beside the prompt chip (Justin 2026-09-09 mock)
        // Generate new (filled) — Justin 2026-09-09 mock; the Edit prompt button was dropped the same night
        regenEl.innerHTML = '<button type="button" class="ai-chat-v2__regen-btn"><svg class="ai-chat-v2__regen-ico" viewBox="0 0 24 24" width="15" height="15" aria-hidden="true" focusable="false"><polyline points="23 4 23 10 17 10" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>Generate new</button>';
        // inside the THREAD, moved into each caption by appendUserMessage (clearThreadResults
        // parks it back here first, so it survives a new set)
        thread.appendChild(regenEl);
        // the finished set already sits in Recently Generated — clear the row and hand the page
        // back to the box (Justin 2026-09-09). Edit prompt does the same but refills the box with
        // the last prompt so it can be tweaked and re-run.
        var restart = function (prefill) {
          var cap = thread.querySelector('.ai-chat-v2__caption');
          var last = cap ? (cap.dataset.prompt || '') : '';
          exitEditMode();
          clearThreadResults();
          panel.classList.remove('is-regen', 'has-results');
          textarea.value = prefill ? last : ''; syncCount();
          autosizeTextarea();
          updateGenerateEnabled();
          try {
            textarea.focus();
            if (prefill) textarea.setSelectionRange(textarea.value.length, textarea.value.length);
          } catch (_) {}
        };
        regenEl.querySelector('.ai-chat-v2__regen-btn').addEventListener('click', function () { restart(false); });
        // Hosts can put the panel back to its first-open state: the post-upload "Create with
        // AI" popup adopts this same panel and re-opened on the LAST prompt + images (Justin
        // 2026-09-10 "make sure it refreshes to initial open"). Same routine as Generate new;
        // Recently Generated stays (it is the persisted history). Skipped mid-generation.
        panel.addEventListener('ai-chat:reset', function () {
          if (generateBtn.classList.contains('is-loading')) return;
          if (!panel.classList.contains('has-results') && !editSourceUrl && !textarea.value) return; // already fresh
          restart(false);
        });
        // (restart(true) — refill the box with the last prompt — stays available for an "Edit prompt" control)
      }
    }

    // v2 (Justin 2026-09-09): a click anywhere OUTSIDE the edit box leaves edit mode, like
    // Cancel. Bubble phase on purpose — the box, the tile Edit links and the zoom's controls
    // stop propagation themselves, so only genuine outside clicks arrive here.
    if (isV2) {
      document.addEventListener('click', function (e) {
        if (!panel.classList.contains('is-editing')) return;
        var t = e.target;
        if (!t || !t.closest) return;
        if (t.closest('.ai-chat-composer') || t.closest('.ai-pop-tile-edit') || t.closest('.ai-pop-zoom')) return;
        exitEditMode();
      });
    }

    // History scroller wrap + arrows
    var leftArrow = null, rightArrow = null;
    if (historyGrid) {
      var scroller = document.createElement('div');
      scroller.className = 'ai-chat-history-scroller';
      historyGrid.parentNode.insertBefore(scroller, historyGrid);
      scroller.appendChild(historyGrid);
      leftArrow = document.createElement('button');
      leftArrow.type = 'button';
      leftArrow.className = 'ai-chat-history-arrow ai-chat-history-arrow--left';
      leftArrow.setAttribute('aria-label', 'Scroll left');
      leftArrow.innerHTML =
        '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"><path d="M10 12L6 8l4-4"/></svg>';
      rightArrow = document.createElement('button');
      rightArrow.type = 'button';
      rightArrow.className = 'ai-chat-history-arrow ai-chat-history-arrow--right';
      rightArrow.setAttribute('aria-label', 'Scroll right');
      rightArrow.innerHTML =
        '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"><path d="M6 4l4 4-4 4"/></svg>';
      scroller.appendChild(leftArrow);
      scroller.appendChild(rightArrow);
      var stepFor = function () { return Math.max(160, Math.round(historyGrid.clientWidth * 0.6)); };
      leftArrow.addEventListener('click', function () { smoothScrollBy(historyGrid, -stepFor()); });
      rightArrow.addEventListener('click', function () { smoothScrollBy(historyGrid, stepFor()); });
      historyGrid.addEventListener('scroll', function () { updateArrowVisibility(); }, { passive: true });
      try { new ResizeObserver(function () { updateArrowVisibility(); }).observe(historyGrid); } catch (_) {}
    }
    function updateArrowVisibility() {
      if (!historyGrid || !leftArrow || !rightArrow) return;
      var max = historyGrid.scrollWidth - historyGrid.clientWidth;
      var x = historyGrid.scrollLeft;
      leftArrow.classList.toggle('is-visible', x > 4);
      rightArrow.classList.toggle('is-visible', max > 4 && x < max - 4);
    }
    function smoothScrollBy(el, delta) {
      var start = el.scrollLeft;
      var max = el.scrollWidth - el.clientWidth;
      var target = Math.max(0, Math.min(start + delta, max));
      var duration = 220;
      var startTime = performance.now();
      function step() {
        var t = Math.min(1, (performance.now() - startTime) / duration);
        var ease = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
        el.scrollLeft = start + (target - start) * ease;
        if (t < 1) setTimeout(step, 16);
        else updateArrowVisibility();
      }
      step();
    }

    // The newest results grid: scrolling never pushes its TOP above the thread's
    // viewport, so the tiles' Edit / magnifier buttons (upper corners) stay in
    // view even when three big tiles + the follow-up bubble are taller than the
    // thread (Justin 2026-09-04, /pages/welcome: "the edit and magnifying glass
    // icons are cut off when everything loads"). Cleared on the next user
    // prompt so that message scrolls into view normally.
    var anchorGrid = null;
    function scrollThreadToBottom() {
      var target = thread.scrollHeight - thread.clientHeight;
      if (anchorGrid && anchorGrid.isConnected) {
        var gridTop = anchorGrid.getBoundingClientRect().top - thread.getBoundingClientRect().top + thread.scrollTop - 8;
        target = Math.min(target, Math.max(0, gridTop));
      }
      thread.scrollTop = target;
      // Mobile follow-up: the composer + Recently Generated history
      // sit OUTSIDE the .ai-chat-thread (they're siblings, not
      // children), so scrolling the thread doesn't bring them into
      // view if any ancestor is the active scroll container.
      // Walk up from the panel and scroll each scrollable ancestor
      // to its bottom so the input + history land in the viewport
      // on small screens. Scoped to ≤749px so desktop (where the
      // panel is a fixed-size sidebar) is unaffected.
      try {
        if (window.matchMedia && window.matchMedia('(max-width: 749px)').matches) {
          var node = panel;
          while (node && node !== document.body) {
            var cs = window.getComputedStyle(node);
            if (cs && (cs.overflowY === 'auto' || cs.overflowY === 'scroll')) {
              node.scrollTop = node.scrollHeight;
            }
            node = node.parentElement;
          }
          // Target the Recently Generated history (the LAST visible
          // element in the panel) so the user lands with the new
          // images, the input, AND the history toggle all in view.
          // Falls back to the composer, then the panel itself, if
          // history isn't in the DOM yet (e.g. on first render).
          var scrollTarget =
            panel.querySelector('[data-ai-history]') ||
            panel.querySelector('.ai-chat-history') ||
            panel.querySelector('.ai-chat-composer') ||
            panel.querySelector('[data-ai-prompt]');
          if (scrollTarget && typeof scrollTarget.scrollIntoView === 'function') {
            scrollTarget.scrollIntoView({ block: 'end', behavior: 'smooth' });
          }
        }
      } catch (_) { /* defensive — never block thread scroll on a follow-up failure */ }
    }

    function updateScrolledState() {
      var distanceFromBottom = thread.scrollHeight - thread.scrollTop - thread.clientHeight;
      var isScrolled = thread.scrollTop > 4 && distanceFromBottom > 4;
      panel.classList.toggle('is-scrolled', isScrolled);
    }
    thread.addEventListener('scroll', updateScrolledState, { passive: true });

    function autosizeTextarea() {
      if (isV2 && wrap) {
        // v2 (Justin 2026-09-09 mock): the prompt card always stacks — eyebrow, text, footer
        // band with Generate — so the box simply grows with the text. Empty = CSS decides
        // (textarea:placeholder-shown caps it to one line); height 0 first so scrollHeight
        // is the content height, not the rows="2" floor.
        wrap.classList.remove('is-multiline');
        if (!textarea.value.trim()) { textarea.style.height = ''; return; }
        textarea.style.height = '0px';
        textarea.style.height = textarea.scrollHeight + 'px';
        return;
      }
      // Always set 'auto' first so the theme's global textarea height is overridden.
      textarea.style.height = 'auto';
      if (!textarea.value.trim()) {
        // Empty: leave height as 'auto' so CSS min-height:32px controls the size.
        if (wrap) {
          wrap.classList.remove('is-multiline');
          // Capture single-line baseline here — only valid when textarea is empty.
          if (!wrap.__singleLineHeight && textarea.scrollHeight > 0) {
            wrap.__singleLineHeight = textarea.scrollHeight;
          }
        }
        return;
      }
      if (!wrap) { textarea.style.height = textarea.scrollHeight + 'px'; return; }
      var singleH = wrap.__singleLineHeight || 32;
      // Always measure at row-mode (narrow) width to decide when to switch.
      // Strip is-multiline temporarily — reading scrollHeight forces a synchronous
      // reflow so the measurement is at narrow width. Then re-add if text wraps.
      // This way the trigger is "wraps in row mode" not "wraps at full width",
      // so the button moves below as soon as the user hits the second line.
      // No oscillation: intermediate class state is never painted (JS batches DOM
      // mutations; only the final state reaches the renderer).
      wrap.classList.remove('is-multiline');
      var rowH = textarea.scrollHeight;
      if (rowH > singleH + 4) {
        // Text wraps at row-mode width — switch to column, measure at full width.
        wrap.classList.add('is-multiline');
        textarea.style.height = textarea.scrollHeight + 'px';
      } else {
        // Fits on 1 line in row mode — stay row mode.
        textarea.style.height = rowH + 'px';
      }
    }

    var _typewriterTimer = null;
    function appendUserMessage(promptText) {
      var msg = document.createElement('div');
      if (isV2) {
        msg.className = 'ai-chat-v2__caption';
        // the prompt is a pale chip and "Generate new" rides right after it on the same
        // line (Justin 2026-09-09 mock): the regen element moves into each new caption and
        // clearThreadResults parks it back in the thread so it survives the clear
        // prompt BAR (Justin 2026-09-09 mock): sparkle badge · "YOUR PROMPT" eyebrow over the prompt ·
        // Edit prompt + Generate new on the right (the regen element carries both buttons)
        msg.dataset.prompt = promptText;
        var badge = document.createElement('span');
        badge.className = 'ai-chat-v2__badge';
        badge.setAttribute('aria-hidden', 'true');
        badge.innerHTML = '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2 13.6 9.1 20.5 11 13.6 12.9 12 20 10.4 12.9 3.5 11 10.4 9.1 12 2Z"/><path d="M19 3 19.7 5.3 22 6 19.7 6.7 19 9 18.3 6.7 16 6 18.3 5.3 19 3Z" opacity=".75"/></svg>';
        var chip = document.createElement('span');
        chip.className = 'ai-chat-v2__caption-text';
        chip.title = promptText;
        var eyebrow = document.createElement('small');
        eyebrow.className = 'ai-chat-v2__caption-eyebrow';
        eyebrow.textContent = 'Your prompt'; // still follows every Edit with AI prompt (Justin 2026-09-09: keep logic, copy stays YOUR PROMPT)
        var big = document.createElement('b');
        big.className = 'ai-chat-v2__caption-prompt';
        big.textContent = promptText;
        chip.appendChild(eyebrow);
        chip.appendChild(big);
        msg.appendChild(badge);
        msg.appendChild(chip);
        if (regenEl) msg.appendChild(regenEl);
        thread.appendChild(msg);
        anchorGrid = null;
        scrollThreadToBottom();
        return msg;
      }
      msg.className = 'ai-chat-msg ai-chat-msg--user';
      var bubble = document.createElement('div');
      bubble.className = 'ai-chat-bubble';
      bubble.textContent = promptText;
      msg.appendChild(bubble);
      thread.appendChild(msg);
      anchorGrid = null;
      scrollThreadToBottom();
      return msg;
    }

    function appendAiMessage(skeletonCount) {
      var msg = document.createElement('div');
      msg.className = 'ai-chat-msg ai-chat-msg--ai';
      var grid = document.createElement('div');
      grid.className = 'ai-pop-grid';
      var n = skeletonCount || batchSize;
      for (var i = 0; i < n; i++) {
        var sk = document.createElement('div');
        sk.className = 'ai-pop-tile is-skeleton';
        grid.appendChild(sk);
      }
      msg.appendChild(grid);
      thread.appendChild(msg);
      panel.classList.add('has-results'); // v2: placeholders give way to results
      anchorGrid = grid;
      scrollThreadToBottom();
      return { msg: msg, grid: grid };
    }

    // ── v2 concept row helpers (Justin 2026-09-08) ─────────────────────────
    // The results grid is a 3-up horizontal scroller: an edit appends a tile to
    // the CURRENT row, arrows (prev/next) appear once there are more tiles than
    // fit, and a NEW prompt replaces the row (its images already live in
    // Recently Generated via addToHistory).
    function clearThreadResults() {
      // park "Generate new" back in the thread first — it rides inside the caption (v2)
      if (regenEl && regenEl.parentNode) thread.appendChild(regenEl);
      Array.prototype.forEach.call(
        thread.querySelectorAll('.ai-chat-msg, .ai-chat-v2__caption'),
        function (el) { el.parentNode && el.parentNode.removeChild(el); }
      );
      anchorGrid = null;
    }
    function lastConceptGrid() {
      var grids = thread.querySelectorAll('.ai-pop-grid');
      return grids.length ? grids[grids.length - 1] : null;
    }
    function gridStep(grid) {
      var c = grid.children;
      return c.length > 1 ? (c[1].offsetLeft - c[0].offsetLeft) : grid.clientWidth;
    }
    function revealGridStart(grid) {
      try { grid.scrollTo({ left: 0, behavior: 'smooth' }); } catch (_) { grid.scrollLeft = 0; }
    }
    function updateNavState(grid) {
      var msg = grid.parentNode;
      if (!msg) return;
      var prev = msg.querySelector('.ai-chat-v2__nav--prev');
      var next = msg.querySelector('.ai-chat-v2__nav--next');
      var max = grid.scrollWidth - grid.clientWidth - 1;
      if (prev) prev.disabled = grid.scrollLeft <= 0;
      if (next) next.disabled = grid.scrollLeft >= max;
    }
    function syncConceptNav(grid) {
      if (!isV2 || !grid) return;
      var msg = grid.parentNode;
      if (!msg) return;
      var visible = panel.classList.contains('ai-chat--narrow') ? 2 : 3; // 3-up row (Justin 2026-09-09, back from 4)
      var more = grid.children.length > visible;
      msg.classList.toggle('has-more', more);
      if (!more) return;
      if (!msg.querySelector('.ai-chat-v2__nav')) {
        ['prev', 'next'].forEach(function (dir) {
          var b = document.createElement('button');
          b.type = 'button';
          b.className = 'ai-chat-v2__nav ai-chat-v2__nav--' + dir;
          b.setAttribute('aria-label', dir === 'prev' ? 'Previous concepts' : 'More concepts');
          b.innerHTML = '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="'
            + (dir === 'prev' ? 'M10 3L5 8l5 5' : 'M6 3l5 5-5 5') + '"/></svg>';
          b.addEventListener('click', function () {
            var step = gridStep(grid);
            try { grid.scrollBy({ left: dir === 'prev' ? -step : step, behavior: 'smooth' }); }
            catch (_) { grid.scrollLeft += dir === 'prev' ? -step : step; }
          });
          msg.appendChild(b);
        });
        grid.addEventListener('scroll', function () { updateNavState(grid); }, { passive: true });
      }
      updateNavState(grid);
    }

    function appendAiTextMessage(text, onDone) {
      var msg = document.createElement('div');
      if (isV2) { onDone && onDone(); return msg; } // v2: no assistant bubbles
      msg.className = 'ai-chat-msg ai-chat-msg--ai';
      var bubble = document.createElement('div');
      bubble.className = 'ai-chat-bubble';
      msg.appendChild(bubble);
      thread.appendChild(msg);
      if (_typewriterTimer) clearTimeout(_typewriterTimer);
      var i = 0;
      var SPEED = 6;
      function step() {
        if (i >= text.length) { onDone && onDone(); return; }
        bubble.textContent = text.slice(0, ++i);
        scrollThreadToBottom();
        _typewriterTimer = setTimeout(step, SPEED);
      }
      step();
      return msg;
    }

    function fillGrid(grid, images, prompt) {
      grid.innerHTML = '';
      if (!images.length) {
        showGridError(grid, 'No images returned — try describing your design differently.', null);
        return;
      }
      images.forEach(function (img) { grid.appendChild(createTile(img)); });
      if (isV2) {
        syncConceptNav(grid);
        // the "Generate new" card replaces the box only once EVERY tile image has
        // loaded (Justin 2026-09-08) — until then the box shows its Generating… state
        var imgs = grid.querySelectorAll('img');
        var pendingImgs = imgs.length;
        var oneImg = function () { if (--pendingImgs <= 0) panel.classList.add('is-regen'); };
        if (!pendingImgs) panel.classList.add('is-regen');
        Array.prototype.forEach.call(imgs, function (im) {
          if (im.complete && im.naturalWidth) { oneImg(); return; }
          im.addEventListener('load', oneImg, { once: true });
          im.addEventListener('error', oneImg, { once: true });
        });
      }
      anchorGrid = grid;
      // Tile heights settle as the images decode — keep the grid top in view.
      Array.prototype.forEach.call(grid.querySelectorAll('img'), function (im) {
        if (!im.complete) im.addEventListener('load', scrollThreadToBottom, { once: true });
      });
      addToHistory(images, prompt);
    }

    // ---------- History strip ----------
    function addToHistory(images, prompt) {
      if (!history) return;
      var existing = loadHistory(instanceKey);
      // Dedupe by URL — newest copy wins. realGenerate returns
      // `existing + newImages`, and re-running the same prompt can return
      // already-stored URLs. Without this guard, history accumulates
      // duplicates of the same image, and selecting it once on the picker
      // drops every duplicate onto the fabric canvas at "Use Design" time
      // (the CTA filter matches every history row by URL).
      var next = images.map(function (img) {
        return {
          url: img.url,
          thumbnailUrl: img.thumbnailUrl || img.url,
          fileName: img.fileName || '',
          prompt: prompt || '',
          savedAt: Date.now()
        };
      }).concat(existing);
      var seen = {};
      next = next.filter(function (e) {
        if (!e || !e.url || seen[e.url]) return false;
        seen[e.url] = 1;
        return true;
      });
      saveHistory(instanceKey, next);
      renderHistory();
      // v1 auto-expands Recently Generated so the new tile is seen; v2 leaves it as the
      // user left it — closed by default (Justin 2026-09-09). setHistoryCollapsed is
      // defined later in this scope but the function declaration is hoisted.
      if (!isV2) setHistoryCollapsed(false);
    }

    var selection = new Set();

    function renderHistory() {
      if (!history || !historyGrid) return;
      var list = loadHistory(instanceKey);
      historyGrid.innerHTML = '';
      var urls = new Set(list.map(function (e) { return e.url; }));
      Array.from(selection).forEach(function (u) { if (!urls.has(u)) selection.delete(u); });
      if (!list.length) { updateSelectionBar(); return; }
      list.forEach(function (entry) {
        var tile = document.createElement('button');
        tile.type = 'button';
        tile.className = 'ai-chat-history-tile';
        tile.dataset.url = entry.url;
        if (selection.has(entry.url)) tile.classList.add('is-selected');

        var img = document.createElement('img');
        img.src = entry.thumbnailUrl;
        img.alt = 'AI generation';
        tile.appendChild(img);

        var select = document.createElement('button');
        select.type = 'button';
        select.className = 'ai-chat-history-tile-select';
        if (selection.has(entry.url)) {
          select.classList.add('is-selected');
          select.setAttribute('aria-pressed', 'true');
        } else {
          select.setAttribute('aria-pressed', 'false');
        }
        select.setAttribute('aria-label', selection.has(entry.url) ? 'Deselect AI image' : 'Select AI image');
        select.innerHTML =
          '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3.5 8 6.5 11 12.5 5"/></svg>';
        select.addEventListener('click', function (e) {
          e.stopPropagation();
          // Toggle classes + aria on the existing nodes only — full
          // renderHistory() rebuilds every <img> which makes thumbnails flash
          // on each click. The grid order doesn't change here, so a full
          // re-render is unnecessary.
          var isSelected;
          if (selection.has(entry.url)) {
            selection.delete(entry.url);
            isSelected = false;
          } else {
            // Sticker-style products only support a single image at a time
            // (no multi-design upload pipeline). Clear any previous selection
            // before adding the new pick — produces "radio-button" behavior.
            if (isSingleSelectMode() && selection.size > 0) {
              selection.clear();
              if (historyGrid) {
                historyGrid.querySelectorAll('.ai-chat-history-tile.is-selected').forEach(function (t) {
                  t.classList.remove('is-selected');
                  var sel = t.querySelector('.ai-chat-history-tile-select');
                  if (sel) {
                    sel.classList.remove('is-selected');
                    sel.setAttribute('aria-pressed', 'false');
                    sel.setAttribute('aria-label', 'Select AI image');
                  }
                });
              }
            }
            selection.add(entry.url);
            isSelected = true;
          }
          tile.classList.toggle('is-selected', isSelected);
          select.classList.toggle('is-selected', isSelected);
          select.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
          select.setAttribute('aria-label', isSelected ? 'Deselect AI image' : 'Select AI image');
          updateSelectionBar();
        });
        tile.appendChild(select);

        // Edit (NB refine) — bottom-left icon, sibling to select(✓) /
        // remove(×). stopPropagation so it doesn't toggle the tile's selection.
        // Same dtfut+ds scope as the result-tile Edit button (see hasEditAffordance).
        if (hasEditAffordance()) {
          var histEdit = document.createElement('button');
          histEdit.type = 'button';
          histEdit.className = 'ai-chat-history-tile-edit';
          histEdit.setAttribute('aria-label', 'Edit this image');
          // DO NOT REVERT to stroke="currentColor": this Recently-Generated tile
          // renders inside .product-text on the by-size PDP, where
          // `.product-text button:not(.button){color:inherit}` (section-main-product.css,
          // specificity 0,2,1) overrides `.ai-chat-history-tile-edit{color:#fff}` (0,1,0),
          // so the button inherits the dark #111827 body text color. A currentColor stroke
          // then paints a dark pencil onto the dark rgba(0,0,0,.55) circle = invisible.
          // Explicit white stroke matches the sibling ✓ (.ai-chat-history-tile-select svg{stroke:#fff}).
          histEdit.innerHTML =
            '<svg viewBox="0 0 24 24" fill="none" stroke="#ffffff" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"/></svg>';
          histEdit.addEventListener('click', function (e) {
            e.preventDefault();
            e.stopPropagation();
            enterEditMode(entry.url, entry.thumbnailUrl || entry.url);
          });
          tile.appendChild(histEdit);
        }

        var remove = document.createElement('span');
        remove.className = 'ai-chat-history-tile-remove';
        remove.setAttribute('role', 'button');
        remove.setAttribute('aria-label', 'Remove from history');
        remove.tabIndex = 0;
        remove.textContent = '\u00D7';
        var removeEntry = function (e) {
          e.stopPropagation();
          var next = loadHistory(instanceKey).filter(function (x) { return x.url !== entry.url; });
          saveHistory(instanceKey, next);
          selection.delete(entry.url);
          renderHistory();
        };
        remove.addEventListener('click', removeEntry);
        remove.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') removeEntry(e);
        });
        tile.appendChild(remove);

        // Click anywhere on the tile (image, padding, even the checkbox area)
        // toggles the checkbox. The X (remove) and the checkbox itself have
        // their own handlers with stopPropagation so they don't double-fire.
        tile.addEventListener('click', function () {
          var isSelected;
          if (selection.has(entry.url)) {
            selection.delete(entry.url);
            isSelected = false;
          } else {
            // Sticker-style products only support a single image at a time
            // (no multi-design upload pipeline). Clear any previous selection
            // before adding the new pick.
            if (isSingleSelectMode() && selection.size > 0) {
              selection.clear();
              if (historyGrid) {
                historyGrid.querySelectorAll('.ai-chat-history-tile.is-selected').forEach(function (t) {
                  t.classList.remove('is-selected');
                  var sel = t.querySelector('.ai-chat-history-tile-select');
                  if (sel) {
                    sel.classList.remove('is-selected');
                    sel.setAttribute('aria-pressed', 'false');
                    sel.setAttribute('aria-label', 'Select AI image');
                  }
                });
              }
            }
            selection.add(entry.url);
            isSelected = true;
          }
          tile.classList.toggle('is-selected', isSelected);
          select.classList.toggle('is-selected', isSelected);
          select.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
          select.setAttribute('aria-label', isSelected ? 'Deselect AI image' : 'Select AI image');
          updateSelectionBar();
        });
        historyGrid.appendChild(tile);
      });
      updateSelectionBar();
      updateArrowVisibility();
    }

    // Build the destination URL for the aipage Order-as-DTF / UV-DTF CTAs.
    // Mirrors the my-designs-panel pattern (snippets/my-designs-panel.liquid):
    //   • 1 image  → ?file=<encoded URL>
    //   • 2+ images → ?my_designs_selection=<encoded JSON payload>
    // The destination products (/products/dtf-transfers and
    // /products/uv-dtf-stickers-decals-by-size) already handle both formats
    // because the my-designs flow uses the same query params.
    function buildHandoffUrl(productPath, entries) {
      if (entries.length === 1) {
        return productPath + '?file=' + encodeURIComponent(entries[0].url);
      }
      var payload = entries.map(function (e, i) {
        return {
          id: 'ai-' + Date.now() + '-' + i,
          name: e.fileName || ('ai-image-' + (i + 1) + '.png'),
          fileURL: e.url
        };
      });
      return productPath + '?my_designs_selection=' +
        encodeURIComponent(JSON.stringify(payload));
    }

    // Show a small floating toast while the navigation kicks off.
    function showHandoffToast(count) {
      var t = document.createElement('div');
      t.style.cssText = 'position:fixed;left:50%;bottom:32px;transform:translateX(-50%);background:#0f172a;color:#fff;font:600 14px/1.2 "DM Sans",system-ui,sans-serif;padding:14px 22px;border-radius:999px;box-shadow:0 12px 32px rgba(15,23,42,0.25);z-index:99999;pointer-events:none;';
      t.textContent = 'Sending ' + count + ' design' + (count === 1 ? '' : 's') + '…';
      document.body.appendChild(t);
    }

    function navigateToHandoff(productPath, entries) {
      var url = buildHandoffUrl(productPath, entries);
      showHandoffToast(entries.length);
      setTimeout(function () { window.location.assign(url); }, 250);
      return Promise.resolve();
    }

    var selectionBar = null;
    function ensureSelectionBar() {
      if (selectionBar) return selectionBar;
      var bar = document.createElement('div');
      bar.className = 'ai-chat-history-selection-bar';
      bar.setAttribute('role', 'region');
      bar.setAttribute('aria-label', 'AI image selection actions');
      // Order matters: the primary `.__cta` has `margin-left: auto` (see
      // v0-styles.css), which pushes it to the right edge of the bar.
      // Putting `.__cta-secondary` AFTER `.__cta` in the DOM means the
      // secondary lands flush against the primary's right side, producing
      // the [primary, secondary] right-aligned pair the design calls for.
      bar.innerHTML =
        '<span class="ai-chat-history-selection-bar__count" data-ai-selection-count>0 selected</span>' +
        '<button type="button" class="ai-chat-history-selection-bar__clear" data-ai-selection-clear>Deselect all</button>' +
        '<button type="button" class="ai-chat-history-selection-bar__cta" data-ai-selection-cta>' +
          '<span data-ai-selection-cta-label>Use Designs</span>' +
        '</button>' +
        '<button type="button" class="ai-chat-history-selection-bar__cta-secondary" data-ai-selection-cta-secondary style="display:none">' +
          '<span data-ai-selection-cta-secondary-label>Other Ordering Options</span>' +
        '</button>';
      history.appendChild(bar);
      bar.querySelector('[data-ai-selection-clear]').addEventListener('click', function () {
        selection.clear();
        renderHistory();
      });
      bar.querySelector('[data-ai-selection-cta]').addEventListener('click', function (ev) {
        ev.stopPropagation();
        var urls = Array.from(selection);
        if (!urls.length) return;
        // Dedupe by URL — older builds of addToHistory didn't dedupe, so a
        // user with prior duplicates in localStorage can have N rows for the
        // same URL. Without this, selecting that URL once routes N entries
        // through Use Design and drops N copies on the canvas / cart pipeline.
        var seenUrls = {};
        var entries = loadHistory(instanceKey).filter(function (e) {
          if (!e || !selection.has(e.url) || seenUrls[e.url]) return false;
          seenUrls[e.url] = 1;
          return true;
        });

        // Branch by instance:
        //  - aipage (/pages/ai-image-creator): primary CTA is "Order as DTF".
        //    Navigate to /products/dtf-transfers with `?file=URL` (single) or
        //    `?my_designs_selection=JSON` (multi) — same query-param shape
        //    the my-designs-panel uses, so the destination product handles
        //    both cases without needing a separate code path.
        //  - dtfut (Tab-3 Create with AI on the PDP): hand the full file array
        //    to window.manageFiles in ONE call.
        //  - ds (Design Studio overlay): each image lands on the fabric canvas
        //    independently, so parallel inserts via Promise.all is fine.
        var done = function () { selection.clear(); renderHistory(); };
        if (instanceKey === 'aipage') {
          navigateToHandoff('/products/dtf-transfers', entries).then(done);
        } else if (instanceKey === 'dtfut') {
          // Single-image picks (including all sticker-style products which
          // are forced single-select) route through insertViaCartPipeline
          // — the same path a single-tile click uses. That path uses
          // `manageFiles` / `uploadFile` and works on every variant-A
          // template (vinyl-stickers, magnets, etc.), whereas
          // insertMultipleViaCartPipeline depends on the URL-based
          // `singleFileManageByURL`/`multiFilesManageByURL` helpers from
          // bySize_popular.js which aren't loaded everywhere.
          if (entries.length === 1) {
            insertViaCartPipeline(document.createElement('div'), {
              url: entries[0].url,
              thumbnailUrl: entries[0].thumbnailUrl,
              fileName: entries[0].fileName,
              prompt: entries[0].prompt
            }).then(done);
          } else {
            // Route multi-select through `md-picker:use-designs` so the
            // unified interceptor in dtf-ai-modal.js can apply reload-and-restore
            // when an upload is active. When no upload is active, the bridge
            // handler in product-page-popups-multiupload.liquid takes over and
            // calls multiFilesManageByURL — same end-state as the legacy
            // insertMultipleViaCartPipeline path.
            document.dispatchEvent(new CustomEvent('md-picker:use-designs', {
              bubbles: true,
              detail: {
                items: entries.map(function (entry) {
                  return {
                    file: entry.url,
                    fileName: entry.fileName || 'ai-image.png',
                    id: 'ai-' + (entry.ts || Date.now()),
                    isAIImage: true
                  };
                })
              }
            }));
            done();
          }
        } else {
          /* Pass `idx` as cascadeIndex so each subsequent insert shifts 30px
             diagonally on the fabric canvas (Design Studio overlay). idx=0
             lands at default position, idx=1 at +30/+30, idx=2 at +60/+60, etc. */
          Promise.all(entries.map(function (entry, idx) {
            return realInsertToCanvas(document.createElement('div'), {
              url: entry.url,
              thumbnailUrl: entry.thumbnailUrl,
              fileName: entry.fileName,
              prompt: entry.prompt
            }, instanceKey, idx);
          })).then(done);
        }
      });
      // Secondary CTA — only meaningful for the aipage instance:
      //   1 selected → open the Other Ordering Options modal with that image
      //   2+ selected → navigate to /products/uv-dtf-stickers-decals-by-size
      //                 with `?my_designs_selection=JSON`
      // stopPropagation guards against any document-level click handlers
      // (e.g. md-picker:use-designs capture in dtf-ai-page.js) intercepting
      // the click and re-routing it to the DTF page.
      bar.querySelector('[data-ai-selection-cta-secondary]').addEventListener('click', function (ev) {
        ev.stopPropagation();
        if (instanceKey !== 'aipage') return;
        var urls = Array.from(selection);
        if (!urls.length) return;
        // Same dedupe guard as the primary CTA above — without it, users
        // whose localStorage history has duplicate rows for one URL get
        // entries.length > 1 from a single visual selection, which routes
        // them into the multi-select navigateToHandoff branch (UV-DTF
        // by-size) instead of opening the Other Ordering Options modal.
        var seenUrls = {};
        var entries = loadHistory(instanceKey).filter(function (e) {
          if (!e || !selection.has(e.url) || seenUrls[e.url]) return false;
          seenUrls[e.url] = 1;
          return true;
        });
        var done = function () { selection.clear(); renderHistory(); };
        if (entries.length === 1) {
          document.dispatchEvent(new CustomEvent('aip-oo:open', {
            detail: { imageUrl: entries[0].url }
          }));
        } else {
          navigateToHandoff('/products/uv-dtf-stickers-decals-by-size', entries).then(done);
        }
      });
      selectionBar = bar;
      return bar;
    }
    function updateSelectionBar() {
      var count = selection.size;
      if (!count) {
        if (selectionBar) selectionBar.classList.remove('is-visible');
        if (history) history.classList.remove('is-selecting');
        return;
      }
      var bar = ensureSelectionBar();
      bar.querySelector('[data-ai-selection-count]').textContent = count + ' items selected';

      var primaryLabel = bar.querySelector('[data-ai-selection-cta-label]');
      var secondaryBtn = bar.querySelector('[data-ai-selection-cta-secondary]');
      var secondaryLabel = bar.querySelector('[data-ai-selection-cta-secondary-label]');

      if (instanceKey === 'aipage') {
        // Two-CTA layout exclusive to /pages/ai-image-creator.
        primaryLabel.textContent = 'Order as DTF';
        secondaryLabel.textContent = (count === 1) ? 'Other Ordering Options' : 'Order as UV DTF';
        secondaryBtn.style.display = '';
      } else {
        primaryLabel.textContent = (count === 1) ? 'Use Design' : 'Use Designs';
        secondaryBtn.style.display = 'none';
      }
      bar.classList.add('is-visible');
      history.classList.add('is-selecting');
    }

    function createTile(image) {
      var tile = document.createElement('div');
      tile.className = 'ai-pop-tile';
      tile.dataset.url = image.url;
      if (selection.has(image.url)) tile.classList.add('is-selected');
      var img = document.createElement('img');
      img.src = image.thumbnailUrl || image.url;
      img.alt = 'AI generation';
      tile.appendChild(img);
      // CTA label under each thumbnail. Clicks bubble to the tile's handler
      // so clicking the label does the same as clicking the image.
      var cta = document.createElement('span');
      cta.className = 'ai-pop-tile-cta';
      cta.innerHTML = 'Use this design'
        + '<svg class="ai-pop-tile-cta__arrow" viewBox="0 0 16 16" fill="none" '
        + 'stroke="currentColor" stroke-width="2" stroke-linecap="round" '
        + 'stroke-linejoin="round" aria-hidden="true">'
        + '<path d="M3 8h10M9 4l4 4-4 4"/></svg>';
      tile.appendChild(cta);
      // Magnifier (upper-right of the thumbnail) — opens the full-res design in
      // the lightbox. stopPropagation so it never triggers the tile's select /
      // "Use this design" / insert handler bound just below.
      var zoom = document.createElement('button');
      zoom.type = 'button';
      zoom.className = 'ai-pop-tile-zoom';
      zoom.setAttribute('aria-label', 'Preview full size');
      zoom.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" '
        + 'stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">'
        + '<circle cx="11" cy="11" r="7"/><path d="M21 21l-4.35-4.35"/></svg>';
      zoom.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        openAiZoom(image.url || image.thumbnailUrl || img.src, function () { tile.click(); }); // "Use This Design" in the zoom = the tile's own click
      });
      tile.appendChild(zoom);
      // Edit (NB refine) — upper-left pill, mirrors the zoom button's
      // chrome. Opens edit mode in the composer with this tile as the source.
      // stopPropagation so it never triggers the tile's "Use this design" insert.
      // Scoped to surfaces where the NB editor is loaded — dtfut (PDP
      // Create-with-AI tab) and ds (Design Studio overlay); Phase-1 in-browser
      // confirmed both. runEdit also guards defensively. The standalone
      // /pages/ai-image-creator ('aipage') has no editor, so no Edit there.
      if (hasEditAffordance()) {
        var editBtn = document.createElement('button');
        editBtn.type = 'button';
        editBtn.className = 'ai-pop-tile-edit';
        editBtn.setAttribute('aria-label', 'Edit this image');
        editBtn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" '
          + 'stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">'
          + '<path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"/></svg>'
          // v2 (Justin 2026-09-08): "Edit design with AI"; narrow panels keep the short form (CSS picks)
          + (isV2 ? '<span class="ai-pop-tile-edit__long">Edit design with AI</span><span class="ai-pop-tile-edit__short">Edit with AI</span>' : '<span>Edit</span>');
        editBtn.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          enterEditMode(image.url, image.thumbnailUrl || img.src);
        });
        tile.appendChild(editBtn);
      }
      tile.addEventListener('click', function () {
        // On the standalone AI page, "Use this design" toggles the tile into
        // the same multi-select / selection-bar flow that Recently Generated
        // history tiles use. The selection bar's "Order as DTF" / "Other
        // Ordering Options" CTAs then drive the handoff — gives the user a
        // chance to review or add more before navigating.
        if (instanceKey === 'aipage') {
          var url = image.url;
          if (selection.has(url)) {
            selection.delete(url);
            tile.classList.remove('is-selected');
          } else {
            if (isSingleSelectMode() && selection.size > 0) {
              // Match history-tile radio behavior on sticker-style products.
              var prev = Array.from(selection);
              selection.clear();
              prev.forEach(function (u) {
                var t = document.querySelector('.ai-pop-tile[data-url="' + u + '"]');
                if (t) t.classList.remove('is-selected');
              });
            }
            selection.add(url);
            tile.classList.add('is-selected');
          }
          renderHistory(); /* sync the history strip's selection state */
          updateSelectionBar();
          return;
        }
        realInsertToCanvas(tile, image, instanceKey);
      });
      return tile;
    }

    function showGridError(grid, message, onRetry) {
      var msgEl = grid.parentNode;
      if (msgEl) msgEl.removeChild(grid);

      var wrap = document.createElement('div');
      wrap.style.cssText = 'display:flex;flex-direction:column;gap:8px;align-items:flex-start';

      var bubble = document.createElement('div');
      bubble.className = 'ai-chat-bubble';
      bubble.textContent = message;
      wrap.appendChild(bubble);

      if (onRetry) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'ai-chat-retry-btn';
        btn.textContent = 'Try again';
        btn.addEventListener('click', function () {
          if (msgEl) msgEl.removeChild(wrap);
          var newGrid = document.createElement('div');
          newGrid.className = 'ai-pop-grid';
          for (var i = 0; i < batchSize; i++) {
            var sk = document.createElement('div');
            sk.className = 'ai-pop-tile is-skeleton';
            newGrid.appendChild(sk);
          }
          if (msgEl) msgEl.appendChild(newGrid);
          anchorGrid = newGrid;
          onRetry(newGrid);
        });
        wrap.appendChild(btn);
      }

      if (msgEl) msgEl.appendChild(wrap);
      scrollThreadToBottom();
    }

    function runGenerationIntoGrid(prompt, grid) {
      var hadResults = false;
      realGenerate(prompt, batchSize).then(function (res) {
        var stats = res.stats || {};
        var existing = res.existing || [];
        var newImages = res.newImages || [];
        var remaining = stats.remainingRequest
          ? Math.ceil(stats.remainingRequest / batchSize)
          : Number.MAX_SAFE_INTEGER;
        if (remaining <= 0) {
          showGridError(grid, "You've reached the AI generation limit. Try again later.", null);
          return;
        }
        var combined = existing.concat(newImages);
        fillGrid(grid, combined, prompt);
        hadResults = combined.length > 0;
        scrollThreadToBottom();
      }).catch(function (err) {
        console.error('AI chat generate failed', err);
        showGridError(grid, 'Generation failed — please try again.', function (newGrid) {
          generateBtn.classList.add('is-loading');
          generateBtn.disabled = true;
          runGenerationIntoGrid(prompt, newGrid);
        });
      }).then(function () {
        if (hadResults) {
          appendAiTextMessage(generationDoneMsg());
        }
        generateBtn.classList.remove('is-loading');
        updateGenerateEnabled();
      });
    }

    function generate() {
      var prompt = (textarea.value || '').trim();
      if (errorBox) errorBox.textContent = '';
      if (!prompt) return;

      // Edit mode: refine the chosen source image instead of creating new.
      if (editSourceUrl) { runEdit(prompt, editSourceUrl); return; }

      generateBtn.classList.add('is-loading');
      generateBtn.disabled = true;

      if (isV2) clearThreadResults(); // v2: focus on the new 3 — the previous set stays in Recently Generated
      appendUserMessage(prompt);
      textarea.value = ''; syncCount();
      autosizeTextarea();

      appendAiTextMessage('Generating your Images');
      var ctx = appendAiMessage();
      runGenerationIntoGrid(prompt, ctx.grid);
    }

    // ---------- AI Edit (NB refine) ----------
    // Reuses the existing globals window.nbEditRequest (NB
    // edit) + window.canUseAiRequest (daily-limit gate). The
    // provider result is re-hosted onto Ninja's store (rehostEditedImage)
    // before entering the grid, then flows through the SAME fillGrid()/
    // addToHistory() path as a create so it lands in Recently Generated.
    function showEditChip(thumbUrl) {
      var composer = container.querySelector('.ai-chat-composer');
      if (!composer) return;
      var chip = composer.querySelector('[data-ai-edit-chip]');
      if (!chip) {
        chip = document.createElement('div');
        chip.className = 'ai-chat-edit-chip';
        chip.setAttribute('data-ai-edit-chip', '');
        chip.innerHTML =
          '<img class="ai-chat-edit-chip__thumb" alt="Editing this image">' +
          '<span class="ai-chat-edit-chip__label">Editing this image</span>' +
          '<button type="button" class="ai-chat-edit-chip__cancel" aria-label="Cancel edit">' +
          '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true"><path d="M3 3l10 10M13 3L3 13"/></svg></button>';
        var wrapEl = composer.querySelector('.ai-chat-input-wrap');
        composer.insertBefore(chip, wrapEl || composer.firstChild);
        chip.querySelector('.ai-chat-edit-chip__cancel').addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          exitEditMode();
        });
      }
      var thumb = chip.querySelector('.ai-chat-edit-chip__thumb');
      if (thumb) thumb.src = thumbUrl || '';
      // v2: the box sits under the tile being edited, so the chip is redundant
      // (Justin 2026-09-08 "remove the editing this image section") — the inline
      // style must say none here, it beats the sheet's display:none otherwise.
      chip.style.display = isV2 ? 'none' : 'flex';
    }

    // v2 (Justin 2026-09-08): while editing, the prompt box sits right UNDER the tile
    // row with the chosen tile ringed + badged "Editing", so it is obvious which image
    // is being changed. The composer element itself moves (all refs stay valid) and
    // goes back to its grid cell when edit mode ends.
    // v2 phones (Justin 2026-09-10 "box disappears and nothing changes"): with the keyboard
    // up, tapping Apply edit / Cancel blurs the textarea FIRST; iOS then scrolls the page back
    // and dispatches the click to whatever slid under the finger — an OUTSIDE click, so the
    // outside-click rule exited edit mode and the box vanished with nothing sent. Act on the
    // touch itself (touchend targets the element the finger went DOWN on, whatever moved
    // since) and cancel the synthetic click so nothing can land outside the box.
    function armTouchTap(btn, fn) {
      if (!btn || btn.__v2TouchTap || !('ontouchstart' in window)) return;
      btn.__v2TouchTap = true;
      var start = null;
      btn.addEventListener('touchstart', function (e) {
        var t = e.touches && e.touches[0];
        start = t ? { x: t.clientX, y: t.clientY } : null;
      }, { passive: true });
      btn.addEventListener('touchend', function (e) {
        if (!isV2 || !editSourceUrl || btn.disabled) return; // only the edit box needs this
        var t = e.changedTouches && e.changedTouches[0];
        if (start && t && (Math.abs(t.clientX - start.x) > 12 || Math.abs(t.clientY - start.y) > 12)) return; // a scroll, not a tap
        e.preventDefault(); // no synthetic click
        try { textarea.blur(); } catch (_) {} // commits iOS autocorrect and puts the keyboard away
        fn();
      });
    }
    var composerHome = null;
    function inlineComposerFor(srcUrl) {
      var composerEl = container.querySelector('.ai-chat-composer');
      var tiles = thread.querySelectorAll('.ai-pop-tile');
      var editTile = null;
      Array.prototype.forEach.call(tiles, function (t) {
        t.classList.remove('is-editing');
        if (!editTile && t.dataset.url === srcUrl) editTile = t;
      });
      if (!editTile || !composerEl) return;
      editTile.classList.add('is-editing');
      if (!composerHome) composerHome = { parent: composerEl.parentNode, next: composerEl.nextSibling };
      // the box sits UNDER the whole row at full width, every width (Justin 2026-09-08
      // "make the edit with ai text box full width"); the ring + check on the tile — and
      // the blur on the others — say which design is being edited
      var msgEl = editTile.parentNode && editTile.parentNode.parentNode; // grid -> msg
      if (msgEl && msgEl.parentNode) msgEl.parentNode.insertBefore(composerEl, msgEl.nextSibling);
      else editTile.appendChild(composerEl);
      composerEl.classList.add('ai-chat-composer--inline', 'ai-chat-composer--below');
      // Cancel under Apply edit (Justin 2026-09-08 "there needs to be a cancel button
      // that takes them back to where they were") — the chip's × is gone on v2
      var wrapEl = composerEl.querySelector('.ai-chat-input-wrap');
      if (wrapEl && !wrapEl.querySelector('[data-ai-v2-cancel]')) {
        var cancelBtn = document.createElement('button');
        cancelBtn.type = 'button';
        cancelBtn.className = 'ai-chat-v2__cancel';
        cancelBtn.setAttribute('data-ai-v2-cancel', '');
        cancelBtn.textContent = 'Cancel';
        cancelBtn.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); exitEditMode(); });
        armTouchTap(cancelBtn, exitEditMode);
        wrapEl.appendChild(cancelBtn);
      }
      if (!composerEl.__v2Stop) {
        // the tile's own click = "use this design" — never let the box's clicks reach it
        composerEl.addEventListener('click', function (e) { e.stopPropagation(); });
        composerEl.__v2Stop = true;
      }
      panel.classList.add('is-editing');
    }
    function restoreComposer() {
      Array.prototype.forEach.call(thread.querySelectorAll('.ai-pop-tile.is-editing'), function (t) { t.classList.remove('is-editing'); });
      var composerEl = container.querySelector('.ai-chat-composer');
      if (composerEl && composerHome && composerEl.classList.contains('ai-chat-composer--inline')) {
        composerHome.parent.insertBefore(composerEl, composerHome.next);
        composerEl.classList.remove('ai-chat-composer--inline', 'ai-chat-composer--below');
      }
      panel.classList.remove('is-editing');
    }

    function enterEditMode(srcUrl, thumbUrl) {
      if (!srcUrl) return;
      // v2: Edit on the tile already being edited toggles the box away
      if (isV2 && editSourceUrl === srcUrl && panel.classList.contains('is-editing')) { exitEditMode(); return; }
      editSourceUrl = srcUrl;
      showEditChip(thumbUrl || srcUrl);
      if (isV2) inlineComposerFor(srcUrl);
      if (sendLabelEl) {
        sendLabelEl.textContent = isV2 ? 'Apply edit' : 'Edit';
        if (sendLabelEl.hasAttribute('data-loading')) sendLabelEl.setAttribute('data-loading', 'Editing…'); // v2 loading copy
      }
      textarea.setAttribute('placeholder', isV2 ? 'What should change? e.g. make the dog a husky, add snow…' : 'Describe your change…');
      textarea.value = ''; syncCount();
      autosizeTextarea();
      updateGenerateEnabled();
      if (isV2) {
        // v2 (Justin 2026-09-09 "focus the scroll down to the edit with ai text entry"): ONE
        // scroll, to the box, instant, BEFORE focusing. The legacy scrollThreadToBottom() also
        // scrolled phones to Recently Generated (below the box) and the two smooth scrolls
        // raced the iOS keyboard — on an iPhone the box ended up off-screen and Edit with AI
        // looked dead (Justin 2026-09-10, PDP pre-upload). preventScroll keeps the focus
        // from yanking the page again; iOS still nudges the field above the keyboard.
        var box = container.querySelector('.ai-chat-composer--inline');
        if (box && typeof box.scrollIntoView === 'function') {
          try { box.scrollIntoView({ block: 'center' }); } catch (_) { box.scrollIntoView(); }
        }
        try { textarea.focus({ preventScroll: true }); } catch (_) { try { textarea.focus(); } catch (__) {} }
      } else {
        try { textarea.focus(); } catch (_) {}
        scrollThreadToBottom();
      }
    }

    function exitEditMode() {
      editSourceUrl = null;
      if (isV2) restoreComposer();
      var chip = container.querySelector('[data-ai-edit-chip]');
      if (chip) chip.style.display = 'none';
      if (sendLabelEl) {
        sendLabelEl.textContent = generateBtn.getAttribute('data-ai-generate-label') || 'Generate';
        if (sendLabelEl.hasAttribute('data-loading')) sendLabelEl.setAttribute('data-loading', 'Generating…');
      }
      textarea.setAttribute('placeholder', defaultPromptPlaceholder);
      updateGenerateEnabled();
    }

    function runEdit(prompt, srcUrl) {
      if (typeof window.canUseAiRequest === 'function' && !window.canUseAiRequest()) {
        if (errorBox) errorBox.textContent = 'You have reached your AI limit for today.';
        return;
      }
      if (typeof window.nbEditRequest !== 'function') {
        if (errorBox) errorBox.textContent = 'AI editing is unavailable right now.';
        return;
      }
      generateBtn.classList.add('is-loading');
      generateBtn.disabled = true;
      // v2 (Justin 2026-09-08): the edit lands as the FIRST concept in the CURRENT
      // row ('make new image to the left') — the row scrolls 3-up with arrows — no
      // new row, no caption. Falls back to the classic new-row path when there is no row yet.
      var rowGrid = isV2 ? lastConceptGrid() : null;
      if (rowGrid) {
        // the bar shows the LAST prompt — an edit's words replace the original's (Justin 2026-09-09)
        var capEl = thread.querySelector('.ai-chat-v2__caption');
        if (capEl) {
          capEl.dataset.prompt = prompt;
          var capPrompt = capEl.querySelector('.ai-chat-v2__caption-prompt');
          var capText = capEl.querySelector('.ai-chat-v2__caption-text');
          if (capPrompt) capPrompt.textContent = prompt;
          if (capText) capText.title = prompt;
        }
        textarea.value = ''; syncCount();
        autosizeTextarea();
        exitEditMode();
        var skel = document.createElement('div');
        skel.className = 'ai-pop-tile is-skeleton';
        rowGrid.insertBefore(skel, rowGrid.firstChild);
        syncConceptNav(rowGrid);
        revealGridStart(rowGrid);
        window.nbEditRequest(srcUrl, prompt)
          .then(function (falUrl) { return rehostEditedImage(falUrl); })
          .then(function (hosted) {
            var img = { url: hosted.url, thumbnailUrl: hosted.url, fileName: hosted.fileName };
            var tile = createTile(img);
            if (skel.parentNode === rowGrid) rowGrid.replaceChild(tile, skel); else rowGrid.insertBefore(tile, rowGrid.firstChild);
            addToHistory([img], prompt);
            syncConceptNav(rowGrid);
            revealGridStart(rowGrid);
          })
          .catch(function (err) {
            console.error('ai-chat-sandbox edit failed', err);
            if (skel.parentNode) skel.parentNode.removeChild(skel);
            syncConceptNav(rowGrid);
            if (errorBox) errorBox.textContent = 'Edit failed — try again, or describe a new design.';
          })
          .then(function () {
            generateBtn.classList.remove('is-loading');
            updateGenerateEnabled();
          });
        return;
      }
      appendUserMessage(prompt);
      textarea.value = ''; syncCount();
      autosizeTextarea();
      exitEditMode();
      appendAiTextMessage('Editing your image');
      // Edit returns exactly one image (nbEditRequest num_images:1), so
      // show a single skeleton — the 3-skeleton default is for the create batch.
      var ctx = appendAiMessage(1);
      var grid = ctx.grid;
      window.nbEditRequest(srcUrl, prompt)
        .then(function (falUrl) { return rehostEditedImage(falUrl); })
        .then(function (hosted) {
          fillGrid(grid, [{ url: hosted.url, thumbnailUrl: hosted.url, fileName: hosted.fileName }], prompt);
          appendAiTextMessage('Edited image ready — refine it again or tap “Use this design.”');
          generateBtn.classList.remove('is-loading');
          updateGenerateEnabled();
          scrollThreadToBottom();
        })
        .catch(function (err) {
          console.error('ai-chat-sandbox edit failed', err);
          showGridError(grid, 'Edit failed — try again, or describe a new design.', null);
          generateBtn.classList.remove('is-loading');
          updateGenerateEnabled();
        });
    }

    function updateGenerateEnabled() {
      var hasText = !!textarea.value.trim();
      generateBtn.disabled = !hasText;
      generateBtn.classList.toggle('is-empty', !hasText);
      if (wrap) wrap.classList.toggle('has-text', hasText);
      if (clearBtn) clearBtn.hidden = !hasText;
    }

    // Pill flow: each example pill resolves to a FIXED, predictable set of
    // Ninja-hosted image URLs via getPillImages() — same prompt always yields
    // the same 3 designs, in every environment, with no network dependency.
    // Caps to 3 to match SKELETON_COUNT and enforces a minimum loading state
    // so the skeleton is always visible. For any prompt NOT in the pinned map
    // (defensive — all current pills are pinned) it falls back to the legacy
    // third-party cache and then deterministic picsum tiles. Real textarea
    // generations still go through generate()/realGenerate() unchanged.
    function fakeGenerate(prompt, skeletonDelay) {
      generateBtn.classList.add('is-loading');
      generateBtn.disabled = true;
      if (isV2) clearThreadResults();
      appendUserMessage(prompt);
      textarea.value = ''; syncCount();
      autosizeTextarea();

      var startTime = Date.now();
      var MIN_LOAD_MS = skeletonDelay || 1000;
      var PILL_CAP = 3;

      appendAiTextMessage('Generating your Images');
      var ctx = appendAiMessage();
      var grid = ctx.grid;

      // Render `images` into the grid after honoring the minimum skeleton time.
      function renderInto(images) {
        var elapsed = Date.now() - startTime;
        var wait = Math.max(0, MIN_LOAD_MS - elapsed);
        setTimeout(function () {
          fillGrid(grid, images, prompt);
          appendAiTextMessage(generationDoneMsg());
          generateBtn.classList.remove('is-loading');
          updateGenerateEnabled();
          scrollThreadToBottom();
        }, wait);
      }

      // Predictable path: curated, fixed URLs for the known example pills.
      var pinned = getPillImages(prompt);
      if (pinned) {
        renderInto(pinned.slice(0, PILL_CAP));
        return;
      }

      // Fallback path (unknown prompt): legacy third-party cache, then picsum.
      var AI_HOST = 'https://www.rushordertees.com/design';
      fetch(
        AI_HOST + '/studio/getGenerativeAiUploads.php?prompt=' + encodeURIComponent(prompt),
        { credentials: 'include' }
      )
        .then(function (r) { return r.ok ? r.json() : []; })
        .catch(function () { return []; })
        .then(function (data) {
          // Apply the same 30-day TTL the localStorage history uses so pill
          // cache results stay consistent with what we treat as "fresh"
          // elsewhere. created_at is in unix seconds.
          var cutoffSec = (Date.now() - HISTORY_TTL_MS) / 1000;
          var arr = (Array.isArray(data) ? data : [])
            .filter(function (img) { return !img.created_at || img.created_at > cutoffSec; })
            .slice(0, PILL_CAP);
          var images;
          if (arr.length) {
            images = arr.map(function (img) {
              return {
                url: img.url,
                thumbnailUrl: img.thumbnailUrl || img.url,
                fileName: img.fileName || ''
              };
            });
          } else {
            // Deterministic picsum so the demo still works without a cache hit.
            var seed = (prompt || 'demo').toLowerCase().replace(/\W+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60) || 'demo';
            images = [1, 2, 3].map(function (n) {
              var url = 'https://picsum.photos/seed/' + seed + '-' + n + '/512/512';
              return { url: url, thumbnailUrl: url, fileName: seed + '-' + n + '.jpg' };
            });
          }
          renderInto(images);
        });
    }

    // Wire up
    updateGenerateEnabled();
    autosizeTextarea();
    requestAnimationFrame(autosizeTextarea);
    renderHistory();
    // On first load, expand the section if there are saved designs so the
    // user sees them without needing to click "Show". Empty history stays
    // collapsed (and is also display:none via CSS :has empty grid).
    if (isV2) {
      setHistoryCollapsed(true); // v2 defaults CLOSED (Justin 2026-09-09 "default Recently generated to closed")
    } else if (loadHistory(instanceKey).length > 0) {
      setHistoryCollapsed(false);
    }

    // Cross-instance history sync — re-render this panel's Recently Generated
    // grid whenever ANY AI instance on the page saves to history. Required so
    // a generation in the DS overlay shows up in the dtfut tab grid (and vice
    // versa) without a page reload.
    document.addEventListener('ninjaAiHistory:changed', renderHistory);

    if (pillsRow) {
      pillsRow.querySelectorAll('[data-ai-pill-prompt]').forEach(function (pill) {
        pill.addEventListener('click', function () {
          var prompt = pill.getAttribute('data-ai-pill-prompt') || '';
          fakeGenerate(prompt, 2500);
        });
      });
    }

    if (historyClear) {
      historyClear.addEventListener('click', function () {
        saveHistory(instanceKey, []);
        renderHistory();
      });
    }

    function setHistoryCollapsed(collapsed) {
      if (history) history.classList.toggle('is-collapsed', collapsed);
      panel.classList.toggle('is-history-open', !collapsed);
      if (!collapsed) {
        updateArrowVisibility();
        requestAnimationFrame(function () {
          requestAnimationFrame(updateArrowVisibility);
        });
      }
      if (!historyToggle) return;
      historyToggle.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
      historyToggle.setAttribute(
        'aria-label',
        collapsed ? 'Show recently generated' : 'Hide recently generated'
      );
      if (historyToggleLabel) {
        historyToggleLabel.textContent = collapsed ? (isV2 ? 'Show Recently Generated' : 'Show') : 'Hide'; // v2 spells it out when closed (Justin 2026-09-09)
      }
    }
    if (historyToggle) {
      historyToggle.addEventListener('click', function () {
        setHistoryCollapsed(!history.classList.contains('is-collapsed'));
      });
    }
    // Make the whole "Recently Generated" header bar clickable, not just
    // the small Show/Hide toggle on the right. Excludes the toggle itself
    // (which has its own listener above) so we don't double-fire.
    var historyHead = container.querySelector('.ai-chat-history-head');
    if (historyHead && history) {
      historyHead.style.cursor = 'pointer';
      historyHead.addEventListener('click', function (e) {
        if (e.target.closest('[data-ai-history-toggle]')) return;
        setHistoryCollapsed(!history.classList.contains('is-collapsed'));
      });
    }

    if (wrap) {
      wrap.addEventListener('click', function (e) {
        if (e.target.closest('.ai-chat-send')) return;
        textarea.focus();
      });
    }
    generateBtn.addEventListener('click', function (e) { e.preventDefault(); generate(); });
    armTouchTap(generateBtn, generate); // phones: Apply edit acts on the touch (see armTouchTap)
    if (clearBtn) {
      clearBtn.addEventListener('click', function () {
        textarea.value = ''; syncCount();
        autosizeTextarea();
        updateGenerateEnabled();
        textarea.focus();
      });
    }
    textarea.addEventListener('input', function () {
      autosizeTextarea();
      if (errorBox && errorBox.textContent) errorBox.textContent = '';
      updateGenerateEnabled();
    });
    textarea.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); generate(); }
      if (e.key === 'Escape' && isV2 && editSourceUrl) { e.preventDefault(); exitEditMode(); }
    });

    // Autofocus the textarea when this surface becomes visible.
    var instance = panel.getAttribute('data-ai-instance');
    function focusPrompt() {
      setTimeout(function () { try { textarea.focus(); } catch (_) {} }, 60);
    }
    if (instance === 'aipage') {
      // Standalone page: panel is immediately visible on load.
      focusPrompt();
    } else if (instance === 'dtfut') {
      // Product page tab: focus when the Create with AI tab activates.
      document.addEventListener('dtfut:tab-change', function (e) {
        if (e.detail && e.detail.name === 'ai') {
          requestAnimationFrame(function () {
            syncNarrow();
            autosizeTextarea();
            focusPrompt();
          });
        }
      });
    } else {
      // Design Studio: the deferred ai-create.css is NOT loaded by the DS open
      // path (only the dtfut tab/modal call __ensureAiCss; aipage loads it
      // eagerly). Without it the tile buttons (zoom + Edit) render unstyled —
      // huge SVGs, broken layout. The DS AI panel is revealed far down inside
      // the DS overlay (off-viewport), so the IntersectionObserver below does
      // NOT fire on open (verified in-browser) — it can't be the load trigger.
      // Hook the actual open action instead: a click on EITHER DS "Create with
      // AI" button — the primary toolbar one (#ai-panel-btn) or the blank-canvas
      // / "start from scratch" footer toolbar one (#ai-panel-btn-2). Both open
      // the same AI panel; the -2 button was previously unhooked, so reaching the
      // panel via it (e.g. Skip-and-start-from-scratch -> Create with AI) left
      // ai-create.css unloaded and the tile zoom/Edit icons rendered huge.
      // Bound once at the document level (survives the button being
      // injected/re-rendered), guarded by __aiCssLoaded so it's a no-op after the
      // first load. Preserves the PERF-LOCK deferral (loads on DS panel open, not
      // page load). dtfut/aipage never reach this branch.
      if (!window.__dsAiCssHooked) {
        window.__dsAiCssHooked = true;
        document.addEventListener('click', function (e) {
          if (e.target.closest && e.target.closest('#ai-panel-btn, #ai-panel-btn-2') &&
              window.__ensureAiCss && !window.__aiCssLoaded) {
            window.__ensureAiCss();
          }
        });
      }
      // focus when panel transitions from not visible to visible.
      var _panelVisible = false;
      var _visObs = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting && !_panelVisible) {
            _panelVisible = true;
            focusPrompt();
            requestAnimationFrame(autosizeTextarea);
          } else if (!entry.isIntersecting) {
            _panelVisible = false;
          }
        });
      }, { threshold: 0.1 });
      _visObs.observe(panel);
    }
  }

  // Boot every instance on the page (idempotent).
  function bootAll() {
    document.querySelectorAll('.ai-chat[data-ai-instance]').forEach(attach);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootAll);
  } else {
    bootAll();
  }
  // Shopify theme editor re-render
  document.addEventListener('shopify:section:load', bootAll);
  document.addEventListener('shopify:block:select', bootAll);

  // Ajax-injected panels (e.g. the members-portal tool panes that fetch+inject
  // /pages/ninja-design-studio, /pages/ai-image-generator, … into the page)
  // appear AFTER the one-time bootAll() and do NOT fire shopify:section:load,
  // so they were never attach()-ed: their Create-with-AI generate button stayed
  // unbound → "does nothing" for every tool opened after the first. Observe for
  // newly-added panels and attach them.
  //
  // ⚠ PERF: this runs on a heavy commerce page, so the callback is strictly
  // record-scoped — it only inspects each mutation's addedNodes and, per added
  // ELEMENT, does a single .matches() plus a querySelectorAll scoped to THAT
  // node's own subtree (the small injected fragment). It never rescans the whole
  // document. attach()'s `__aiChatBound` guard makes re-attaching a no-op.
  try {
    var _panelObserver = new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var added = records[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (!node || node.nodeType !== 1) continue; // elements only
          if (node.matches && node.matches('.ai-chat[data-ai-instance]')) {
            attach(node);
          } else if (node.querySelectorAll) {
            var panels = node.querySelectorAll('.ai-chat[data-ai-instance]');
            for (var k = 0; k < panels.length; k++) attach(panels[k]);
          }
        }
      }
    });
    _panelObserver.observe(document.body, { childList: true, subtree: true });
  } catch (_) { /* MutationObserver always available in supported browsers; guarded for safety */ }
})();
